import { useState, useRef, useMemo, useEffect } from "react";
import { api } from "../lib/api";

// All mappable faculty fields grouped by category
const FIELD_GROUPS = [
  { group: "Identity", fields: [
    { key: "title", label: "Title (Dr./Mr./Ms.)" },
    { key: "full_name", label: "Full Name" },
    { key: "dob", label: "Date of Birth" },
    { key: "blood_group", label: "Blood Group" },
    { key: "gender", label: "Gender" },
    { key: "marital_status", label: "Marital Status" },
    { key: "aadhaar", label: "Aadhaar Number" },
    { key: "pan", label: "PAN" },
    { key: "whatsapp", label: "WhatsApp Number" },
    { key: "personal_email", label: "Personal Email" },
    { key: "official_email", label: "Official Email" },
  ]},
  { group: "Address", fields: [
    { key: "corr_house", label: "Corr. House/Street" },
    { key: "corr_area", label: "Corr. Area" },
    { key: "corr_city", label: "Corr. City" },
    { key: "corr_state", label: "Corr. State" },
    { key: "corr_pin", label: "Corr. PIN" },
  ]},
  { group: "Emergency Contact", fields: [
    { key: "emergency_contact_name", label: "Emergency Name" },
    { key: "emergency_contact_relation", label: "Emergency Relation" },
    { key: "emergency_contact_phone", label: "Emergency Phone" },
  ]},
  { group: "Position", fields: [
    { key: "department", label: "Department" },
    { key: "designation", label: "Designation" },
    { key: "joined_at", label: "Date of Joining" },
    { key: "employment_type", label: "Employment Type" },
    { key: "track", label: "Track" },
    { key: "status", label: "Status" },
  ]},
  { group: "Bank & Salary", fields: [
    { key: "ifsc_code", label: "IFSC Code" },
    { key: "bank_name", label: "Bank Name" },
    { key: "bank_branch", label: "Bank Branch" },
    { key: "account_number", label: "Account Number" },
    { key: "monthly_salary", label: "Monthly Salary" },
    { key: "greyt_hr_id", label: "GreytHR ID" },
    { key: "aebas_id", label: "AEBAS ID" },
    { key: "id_card_status", label: "ID Card Status" },
  ]},
  { group: "Qualifications", fields: [
    { key: "ug_course", label: "UG Course" },
    { key: "ug_college", label: "UG College" },
    { key: "ug_complete_year", label: "UG Completion Year" },
    { key: "ug_council", label: "UG Council" },
    { key: "ug_reg_number", label: "UG Reg. Number" },
    { key: "ug_reg_date", label: "UG Reg. Date" },
    { key: "pg_category", label: "PG Category" },
    { key: "pg_title", label: "PG Title/Course" },
    { key: "pg_college", label: "PG College" },
    { key: "pg_complete_year", label: "PG Completion Year" },
    { key: "pg_council", label: "PG Council" },
    { key: "pg_reg_number", label: "PG Reg. Number" },
    { key: "pg_reg_date", label: "PG Reg. Date" },
    { key: "publications", label: "Publications Count" },
    { key: "met_done", label: "MET Completed (true/false)" },
    { key: "bmr_done", label: "BMR Completed (true/false)" },
  ]},
  { group: "NMC", fields: [
    { key: "post_pg_years", label: "Post-PG Years" },
    { key: "years_in_role", label: "Years in Role" },
    { key: "age", label: "Age" },
  ]},
];

const ALL_FIELDS = FIELD_GROUPS.flatMap(g => g.fields);

// Parse dates in multiple formats: "Mon, Jun 15, 2009" | "DD-MM-YYYY" | "YYYY-MM-DD"
function parseIndianDate(v) {
  if (!v) return null;
  const s = String(v).trim();
  // "Mon, Jun 15, 2009" or "Jun 15, 2009" (with or without day-of-week)
  const m0 = s.match(/^(?:[A-Za-z]{3},?\s+)?([A-Za-z]{3})\s+(\d{1,2}),?\s+(\d{4})$/);
  if (m0) {
    const months = {jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11};
    const mi = months[m0[1].toLowerCase()];
    if (mi !== undefined) return new Date(Number(m0[3]), mi, Number(m0[2]));
  }
  // DD-MM-YYYY or DD/MM/YYYY
  const m1 = s.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
  if (m1) return new Date(Number(m1[3]), Number(m1[2]) - 1, Number(m1[1]));
  // YYYY-MM-DD (ISO)
  const m2 = s.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/);
  if (m2) return new Date(Number(m2[1]), Number(m2[2]) - 1, Number(m2[3]));
  return new Date(s); // fallback
}

// Convert any date string to YYYY-MM-DD for storage
function toISODate(v) {
  if (!v) return v;
  const d = parseIndianDate(v);
  if (!d || isNaN(d.getTime())) return v;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const DATE_FIELDS = ["dob", "joined_at", "ug_reg_date", "pg_reg_date"];

// Validation rules per field
const VALIDATORS = {
  pan: (v) => {
    if (!v) return null;
    const s = String(v).toUpperCase().trim();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(s)) return `Invalid PAN format (expected: ABCDE1234F, got: ${s})`;
    return null;
  },
  aadhaar: (v) => {
    if (!v) return null;
    const s = String(v).replace(/\s/g, "");
    if (!/^\d{12}$/.test(s)) return `Aadhaar must be exactly 12 digits (got: ${s.length} chars)`;
    return null;
  },
  ifsc_code: (v) => {
    if (!v) return null;
    const s = String(v).toUpperCase().trim();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(s)) return `Invalid IFSC (expected: SBIN0001234, got: ${s})`;
    return null;
  },
  whatsapp: (v) => {
    if (!v) return null;
    const s = String(v).replace(/[\s\-+]/g, "");
    if (!/^\d{10,13}$/.test(s)) return `Phone must be 10-13 digits (got: ${s})`;
    return null;
  },
  emergency_contact_phone: (v) => {
    if (!v) return null;
    const s = String(v).replace(/[\s\-+]/g, "");
    if (!/^\d{10,13}$/.test(s)) return `Phone must be 10-13 digits (got: ${s})`;
    return null;
  },
  personal_email: (v) => {
    if (!v) return null;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim())) return `Invalid email format`;
    return null;
  },
  official_email: (v) => {
    if (!v) return null;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim())) return `Invalid email format`;
    return null;
  },
  dob: (v) => {
    if (!v) return null;
    const d = parseIndianDate(v);
    if (!d || isNaN(d.getTime())) return `Invalid date (use Mon DD, YYYY e.g. Jun 15, 2009)`;
    if (d > new Date()) return `Date of birth cannot be in the future`;
    return null;
  },
  joined_at: (v) => {
    if (!v) return null;
    const d = parseIndianDate(v);
    if (!d || isNaN(d.getTime())) return `Invalid date (use Mon DD, YYYY e.g. Jul 01, 2018)`;
    return null;
  },
  corr_pin: (v) => {
    if (!v) return null;
    const s = String(v).trim();
    if (!/^[1-9][0-9]{5}$/.test(s)) return `PIN must be 6 digits, cannot start with 0 (got: ${s})`;
    return null;
  },
  monthly_salary: (v) => {
    if (v === undefined || v === null || v === "") return null;
    const n = Number(v);
    if (isNaN(n) || n < 0) return `Salary must be a positive number`;
    return null;
  },
  age: (v) => {
    if (v === undefined || v === null || v === "") return null;
    const n = Number(v);
    if (isNaN(n) || n < 18 || n > 100) return `Age must be between 18-100 (got: ${v})`;
    return null;
  },
  publications: (v) => {
    if (v === undefined || v === null || v === "") return null;
    const n = Number(v);
    if (isNaN(n) || n < 0 || !Number.isInteger(n)) return `Must be a non-negative integer`;
    return null;
  },
  gender: (v) => {
    if (!v) return null;
    const allowed = ["male","female","other","transgender"];
    if (!allowed.includes(String(v).toLowerCase().trim())) return `Expected: Male/Female/Other/Transgender`;
    return null;
  },
  blood_group: (v) => {
    if (!v) return null;
    const allowed = ["A+","A-","B+","B-","AB+","AB-","O+","O-"];
    if (!allowed.includes(String(v).trim())) return `Expected one of: ${allowed.join(", ")}`;
    return null;
  },
  account_number: (v) => {
    if (!v) return null;
    const s = String(v).trim();
    if (!/^\d{5,20}$/.test(s)) return `Account number must be 5-20 digits (got: ${s.length} chars)`;
    return null;
  },
  ug_complete_year: (v) => {
    if (!v) return null;
    const n = Number(v);
    if (isNaN(n) || n < 1950 || n > new Date().getFullYear()) return `Year out of range (1950-${new Date().getFullYear()})`;
    return null;
  },
  pg_complete_year: (v) => {
    if (!v) return null;
    const n = Number(v);
    if (isNaN(n) || n < 1950 || n > new Date().getFullYear()) return `Year out of range (1950-${new Date().getFullYear()})`;
    return null;
  },
};

export default function BulkImport() {
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");

  // Step state: upload → map → preview → result
  const [step, setStep] = useState("upload"); // upload | map | preview | result
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [mapping, setMapping] = useState({}); // csvHeader → facultyField
  const [previewData, setPreviewData] = useState([]); // rows with match info and conflicts
  const [rowDecisions, setRowDecisions] = useState({}); // rowIdx → { fieldKey → 'csv'|'keep' }
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [undoPin, setUndoPin] = useState("");
  const [undoTarget, setUndoTarget] = useState(null); // batch to undo
  const [undoErr, setUndoErr] = useState("");
  const [undoLoading, setUndoLoading] = useState(false);
  const [mode, setMode] = useState("faculty"); // "faculty" | "experience" | "nmc" | "qualifications"
  // Experience import state
  const [expStep, setExpStep] = useState("upload"); // upload | preview | result
  const [expFile, setExpFile] = useState("");
  const [expGroups, setExpGroups] = useState([]); // grouped by faculty
  const [expResult, setExpResult] = useState(null);
  const [expImporting, setExpImporting] = useState(false);
  const fileRef = useRef(null);
  const expFileRef = useRef(null);
  // NMC colleges import state
  const [nmcStep, setNmcStep] = useState("upload"); // upload | preview | result
  const [nmcFile, setNmcFile] = useState("");
  const [nmcRecords, setNmcRecords] = useState([]);
  const [nmcResult, setNmcResult] = useState(null);
  const [nmcImporting, setNmcImporting] = useState(false);
  const [nmcStats, setNmcStats] = useState(null);
  const nmcFileRef = useRef(null);
  // Qualifications import state
  const [qualStep, setQualStep] = useState("upload"); // upload | preview | result
  const [qualFile, setQualFile] = useState("");
  const [qualGroups, setQualGroups] = useState([]); // grouped by faculty
  const [qualDecisions, setQualDecisions] = useState({}); // facultyIdx-field → 'csv'|'keep'
  const [qualResult, setQualResult] = useState(null);
  const [qualImporting, setQualImporting] = useState(false);
  const qualFileRef = useRef(null);
  // AEBAS attendance import state
  const [aebasStep, setAebasStep] = useState("upload"); // upload | preview | result
  const [aebasFile, setAebasFile] = useState("");
  const [aebasRecords, setAebasRecords] = useState([]);
  const [aebasResult, setAebasResult] = useState(null);
  const [aebasImporting, setAebasImporting] = useState(false);
  const aebasFileRef = useRef(null);
  // Aadhaar Restore state
  const [restoreStep, setRestoreStep] = useState("upload"); // upload | preview | importing | result
  const [restoreRecords, setRestoreRecords] = useState([]);
  const [restoreResult, setRestoreResult] = useState(null);
  const restoreFileRef = useRef(null);

  // Load batch history
  const loadBatches = async () => {
    try { const { data } = await api.get("/bulk/batches"); setBatches(data); } catch {}
  };
  const loadNmcStats = async () => {
    try { const { data } = await api.get("/bulk/nmc-stats"); setNmcStats(data); } catch {}
  };
  useEffect(() => { if (pinUnlocked) { loadBatches(); loadNmcStats(); } }, [pinUnlocked]);

  // PIN verification
  const verifyPin = async () => {
    try {
      await api.post("/auth/inspection-pin", { pin });
      setPinUnlocked(true);
    } catch { setPinErr("Invalid PIN"); }
  };

  // Parse CSV
  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      const headers = parseCSVLine(lines[0]);
      const rows = lines.slice(1).map(l => {
        const vals = parseCSVLine(l);
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (vals[i] || "").trim(); });
        return obj;
      }).filter(r => Object.values(r).some(v => v));
      setCsvHeaders(headers);
      setCsvRows(rows);
      // Auto-map headers by fuzzy match
      const autoMap = {};
      headers.forEach(h => {
        const hl = h.toLowerCase().replace(/[^a-z0-9]/g, "");
        const match = ALL_FIELDS.find(f =>
          f.key.toLowerCase().replace(/_/g, "") === hl ||
          f.label.toLowerCase().replace(/[^a-z0-9]/g, "") === hl ||
          hl.includes(f.key.replace(/_/g, "")) ||
          f.label.toLowerCase().replace(/[^a-z0-9]/g, "").includes(hl)
        );
        if (match) autoMap[h] = match.key;
      });
      setMapping(autoMap);
      setStep("map");
    };
    reader.readAsText(file);
  };

  function parseCSVLine(line) {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { result.push(current); current = ""; }
      else { current += ch; }
    }
    result.push(current);
    return result.map(s => s.trim());
  }

  // Build preview — match by Aadhaar
  const buildPreview = async () => {
    const aadhaarCol = Object.entries(mapping).find(([, v]) => v === "aadhaar")?.[0];
    // Get all existing faculty (use match-index for unmasked Aadhaar)
    let existing = [];
    try {
      const { data } = await api.get("/faculty/match-index");
      existing = data;
    } catch {
      try { const { data } = await api.get("/faculty"); existing = data; } catch {}
    }
    const aadhaarMap = {};
    existing.forEach(f => {
      if (f.aadhaar) aadhaarMap[f.aadhaar.replace(/\s/g, "")] = f;
    });

    const preview = csvRows.map((row, idx) => {
      const mappedFields = {};
      Object.entries(mapping).forEach(([csvH, fKey]) => {
        if (row[csvH] !== undefined && row[csvH] !== "") {
          let val = row[csvH];
          // normalize booleans
          if (fKey === "met_done" || fKey === "bmr_done") val = ["true","yes","1","y"].includes(val.toLowerCase());
          // normalize numbers
          if (["monthly_salary","publications","post_pg_years","years_in_role","age"].includes(fKey)) val = Number(val) || val;
          mappedFields[fKey] = val;
        }
      });

      const csvAadhaar = aadhaarCol ? (row[aadhaarCol] || "").replace(/\s/g, "") : "";
      const match = csvAadhaar ? aadhaarMap[csvAadhaar] : null;

      // Find conflicts
      const conflicts = {};
      if (match) {
        Object.entries(mappedFields).forEach(([fKey, csvVal]) => {
          const existingVal = match[fKey];
          if (existingVal !== undefined && existingVal !== null && existingVal !== "" && String(existingVal) !== String(csvVal)) {
            conflicts[fKey] = { existing: existingVal, csv: csvVal };
          }
        });
      }

      return {
        rowIdx: idx,
        rawRow: row,
        mappedFields,
        aadhaar: csvAadhaar,
        matchedFaculty: match,
        isNew: !match,
        conflicts,
        skip: false,
        // Run validation
        errors: Object.entries(mappedFields).reduce((acc, [fKey, val]) => {
          const validator = VALIDATORS[fKey];
          if (validator) {
            const err = validator(val);
            if (err) acc[fKey] = err;
          }
          return acc;
        }, {}),
      };
    });

    setPreviewData(preview);
    // Default decisions: CSV wins for new, keep existing for conflicts
    const decisions = {};
    preview.forEach((p, i) => {
      if (Object.keys(p.conflicts).length > 0) {
        decisions[i] = {};
        Object.keys(p.conflicts).forEach(k => { decisions[i][k] = "csv"; }); // default to CSV overwrites
      }
    });
    setRowDecisions(decisions);
    setStep("preview");
  };

  // Execute import
  const executeImport = async () => {
    setImporting(true);
    let created = 0, updated = 0, skipped = 0, errors = [];
    const createdIds = [];
    const batchId = `batch-${Date.now()}`;

    for (const row of previewData) {
      if (row.skip) { skipped++; continue; }

      const payload = { ...row.mappedFields };

      // Convert DD-MM-YYYY dates to YYYY-MM-DD for storage
      DATE_FIELDS.forEach(k => { if (payload[k]) payload[k] = toISODate(payload[k]); });

      // Apply conflict decisions
      if (rowDecisions[row.rowIdx]) {
        Object.entries(rowDecisions[row.rowIdx]).forEach(([fKey, decision]) => {
          if (decision === "keep") delete payload[fKey];
        });
      }

      // Tag with batch
      payload._import_batch = batchId;

      try {
        if (row.isNew) {
          if (!payload.full_name) { skipped++; continue; }
          const { data } = await api.post("/faculty", payload);
          createdIds.push(data.faculty_id);
          created++;
        } else {
          // For updates, remove empty fields so we don't overwrite existing data with blanks
          const updatePayload = {};
          Object.entries(payload).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") updatePayload[k] = v;
          });
          if (Object.keys(updatePayload).filter(k => k !== "_import_batch").length === 0) { skipped++; continue; }
          await api.patch(`/faculty/${row.matchedFaculty.faculty_id}`, updatePayload);
          updated++;
        }
      } catch (e) {
        errors.push({ row: row.rowIdx + 1, aadhaar: row.aadhaar, error: e?.response?.data?.detail || "Failed" });
      }
    }

    // Save batch record
    try {
      await api.post("/bulk/batch", {
        batch_id: batchId,
        file_name: fileName,
        total: previewData.length,
        created, updated, skipped,
        error_count: errors.length,
        faculty_ids: createdIds,
        fields_mapped: Object.values(mapping).filter(Boolean),
      });
    } catch {}

    setImportResult({ created, updated, skipped, errors, batchId });
    setStep("result");
    setImporting(false);
    loadBatches();
  };

  // Undo a batch
  const executeUndo = async () => {
    setUndoLoading(true); setUndoErr("");
    try {
      const endpoint = undoTarget.batch_type === "experience"
        ? `/bulk/batch/${undoTarget.batch_id}/undo-experience`
        : undoTarget.batch_type === "qualifications"
        ? `/bulk/batch/${undoTarget.batch_id}/undo-qualifications`
        : `/bulk/batch/${undoTarget.batch_id}/undo`;
      await api.post(endpoint, { pin: undoPin });
      setUndoTarget(null);
      loadBatches();
    } catch (e) { setUndoErr(e?.response?.data?.detail || "Failed"); }
    finally { setUndoLoading(false); }
  };

  // ===== EXPERIENCE IMPORT =====
  const handleExpFile = (file) => {
    if (!file) return;
    setExpFile(file.name);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      const headers = parseCSVLine(lines[0]);
      const rows = lines.slice(1).map(l => {
        const vals = parseCSVLine(l);
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (vals[i] || "").trim(); });
        return obj;
      }).filter(r => Object.values(r).some(v => v));

      // Auto-detect column names
      const aadhaarCol = headers.find(h => /aadhaar/i.test(h));
      const titleCol = headers.find(h => /^title$/i.test(h.trim()));
      const nameCol = headers.find(h => /faculty.*name|^name$/i.test(h)) || headers[titleCol ? 2 : 1];
      const desigCol = headers.find(h => /designation/i.test(h) && !/current/i.test(h));
      const fromCol = headers.find(h => /from/i.test(h));
      const toCol = headers.find(h => /\bto\b/i.test(h));
      const instCol = headers.find(h => /institution|college|hospital/i.test(h));

      // Group by Aadhaar (primary key), fallback to name
      const groupMap = {};
      rows.forEach(r => {
        const aadhaar = aadhaarCol ? (r[aadhaarCol] || "").replace(/\s/g, "") : "";
        const title = titleCol ? (r[titleCol] || "").trim() : "";
        const rawName = (r[nameCol] || "").trim();
        const fullName = title ? `${title} ${rawName}` : rawName;
        if (!rawName && !aadhaar) return;
        const key = aadhaar || fullName;
        if (!groupMap[key]) groupMap[key] = { name: fullName, rawName, title, aadhaar, positions: [] };
        if (fullName && !groupMap[key].name) groupMap[key].name = fullName;
        if (rawName && !groupMap[key].rawName) groupMap[key].rawName = rawName;
        if (title && !groupMap[key].title) groupMap[key].title = title;
        if (aadhaar && !groupMap[key].aadhaar) groupMap[key].aadhaar = aadhaar;
        const fromRaw = r[fromCol] || "";
        const toRaw = r[toCol] || "";
        groupMap[key].positions.push({
          designation: r[desigCol] || "",
          from_date: toISODate(fromRaw) || fromRaw,
          to_date: /till date|present|current/i.test(toRaw) ? null : (toISODate(toRaw) || toRaw),
          institution: r[instCol] || "",
          is_current: /till date|present|current/i.test(toRaw),
        });
      });

      // Match to existing faculty
      let existing = [];
      try { const { data } = await api.get("/faculty/match-index"); existing = data; } catch {
        try { const { data } = await api.get("/faculty"); existing = data; } catch {}
      }
      const aadhaarLookup = {};
      const nameLookup = {};
      existing.forEach(f => {
        if (f.aadhaar) aadhaarLookup[f.aadhaar.replace(/\s/g, "")] = f;
        nameLookup[(f.full_name || "").toLowerCase().trim()] = f;
        // Also try with title
        if (f.title) nameLookup[`${f.title} ${f.full_name}`.toLowerCase().trim()] = f;
      });

      const groups = Object.values(groupMap).map(g => {
        const match = (g.aadhaar && aadhaarLookup[g.aadhaar]) ||
                      nameLookup[g.name.toLowerCase().trim()] ||
                      nameLookup[(g.rawName || g.name).toLowerCase().trim()] ||
                      nameLookup[g.name.toLowerCase().replace(/^(dr\.|mr\.|ms\.|mrs\.)\s*/i, "").trim()] || null;
        // Filter out current SSMCH positions (these are already in Step 3)
        const pastPositions = g.positions.filter(p => !p.is_current);
        return { ...g, match, pastPositions, skip: false, existingExpCount: (match?.past_experience || []).length };
      });

      setExpGroups(groups);
      setExpStep("preview");
    };
    reader.readAsText(file);
  };

  const executeExpImport = async () => {
    setExpImporting(true);
    let updated = 0, skipped = 0, errors = [];
    const batchId = `exp-batch-${Date.now()}`;
    const snapshots = []; // for undo: store original past_experience per faculty
    for (const g of expGroups) {
      if (g.skip || !g.match) { skipped++; continue; }
      try {
        const existingExp = g.match.past_experience || [];
        snapshots.push({ faculty_id: g.match.faculty_id, name: g.name, original: existingExp });
        const merged = [...existingExp, ...g.pastPositions.map(p => ({
          designation: p.designation, institution: p.institution,
          from_date: p.from_date, to_date: p.to_date,
          nature_of_employment: "Full-time", nmc_recognised: "Don't know",
          institution_type: "", country: "India",
        }))];
        await api.patch(`/faculty/${g.match.faculty_id}`, { past_experience: merged });
        updated++;
      } catch (e) {
        errors.push({ name: g.name, error: e?.response?.data?.detail || "Failed" });
      }
    }
    // Save batch record
    try {
      await api.post("/bulk/batch", {
        batch_id: batchId,
        batch_type: "experience",
        file_name: expFile,
        total: expGroups.length,
        created: 0, updated, skipped,
        error_count: errors.length,
        faculty_ids: [],
        snapshots,
      });
    } catch {}
    setExpResult({ updated, skipped, errors, total: expGroups.length, batchId });
    setExpStep("result");
    setExpImporting(false);
    loadBatches();
  };

  const downloadExpTemplate = () => {
    const lines = [
      "Aadhaar,Title,Faculty Name,Designation Held,From,To,Institution",
      "FORMAT: 12 digits (primary key),Dr./Mr./Ms./Mrs.,Full Name (without title),Tutor/SR/Asst Prof/Assoc Prof/Prof,\"Mon DD, YYYY\",\"Mon DD, YYYY or Till Date\",Full institution name",
      "234567890123,Dr.,Rajesh Kumar Sharma,Tutor,\"Jun 15, 2009\",\"Dec 13, 2012\",Navodaya Medical College Raichur",
      "234567890123,Dr.,Rajesh Kumar Sharma,Assistant Professor,\"Jul 21, 2015\",\"Jul 22, 2017\",Sukh Sagar Medical College & Hospital Jabalpur",
      "234567890123,Dr.,Rajesh Kumar Sharma,Associate Professor,\"Feb 01, 2025\",Till Date,Sukh Sagar Medical College & Hospital Jabalpur",
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "SSMCH_Experience_Import_Template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // NMC Colleges CSV header → DB field mapping
  const NMC_CSV_MAP = {
    "s no": "sno", "s no.": "sno", "sno": "sno", "sr no": "sno", "serial": "sno",
    "course name": "course_name", "course": "course_name",
    "select a state": "state", "state": "state",
    "college code": "college_code", "code": "college_code",
    "name of the college / medical institution": "college", "name of the college": "college",
    "college name": "college", "college": "college", "medical institution": "college",
    "university name": "university", "university": "university",
    "management of college": "management", "management": "management",
    "year of inception of college": "year_of_inception", "year of inception": "year_of_inception", "inception": "year_of_inception",
    "annual intake (seats)": "annual_intake", "annual intake": "annual_intake", "seats": "annual_intake", "intake": "annual_intake",
    "status of nmc recognition": "nmc_recognition_status", "nmc recognition": "nmc_recognition_status", "recognition status": "nmc_recognition_status",
    "date of lop": "date_of_lop", "lop date": "date_of_lop", "lop": "date_of_lop",
  };

  const parseNmcCsv = (file) => {
    setNmcFile(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      // Parse header
      const rawHeaders = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
      const fieldMap = {};
      rawHeaders.forEach((h, i) => {
        const key = NMC_CSV_MAP[h.toLowerCase().trim()];
        if (key) fieldMap[i] = key;
      });
      // Parse rows
      const records = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = [];
        let cur = "", inQuote = false;
        for (const ch of lines[i]) {
          if (ch === '"') { inQuote = !inQuote; }
          else if (ch === ',' && !inQuote) { cells.push(cur.trim()); cur = ""; }
          else { cur += ch; }
        }
        cells.push(cur.trim());
        const rec = {};
        let hasCollege = false;
        Object.entries(fieldMap).forEach(([idx, field]) => {
          rec[field] = (cells[Number(idx)] || "").replace(/^"|"$/g, "").trim();
          if (field === "college" && rec[field]) hasCollege = true;
        });
        if (hasCollege) records.push(rec);
      }
      setNmcRecords(records);
      setNmcStep("preview");
    };
    reader.readAsText(file);
  };

  const executeNmcImport = async () => {
    setNmcImporting(true);
    try {
      const { data } = await api.post("/bulk/nmc-colleges", { pin, records: nmcRecords });
      setNmcResult(data);
      setNmcStep("result");
      loadNmcStats();
    } catch (e) {
      setNmcResult({ error: e?.response?.data?.detail || "Import failed" });
      setNmcStep("result");
    }
    setNmcImporting(false);
  };

  const downloadNmcTemplate = () => {
    const lines = [
      "S No.,Course Name,Select a State,College Code,Name of the College / Medical Institution,University Name,Management of College,Year of Inception of College,Annual Intake (Seats),Status of NMC Recognition,Date of LOP",
      '1,MBBS,Madhya Pradesh,MC001,"Example Medical College, Bhopal","Barkatullah University, Bhopal",Government,1985,150,Recognized,01-01-2020',
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "NMC_Colleges_Import_Template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ═══════ QUALIFICATIONS IMPORT ═══════

  const QUAL_LEVEL_MAP = {
    UG: {
      course: "ug_course", college: "ug_college", year: "ug_complete_year",
      council: "ug_council", reg_number: "ug_reg_number", reg_date: "ug_reg_date", category: null, flag: null,
    },
    PG: {
      course: "pg_title", college: "pg_college", year: "pg_complete_year",
      council: "pg_council", reg_number: "pg_reg_number", reg_date: "pg_reg_date", category: "pg_category", flag: null,
    },
    Diploma: {
      course: "diploma_title", college: "diploma_institute", year: "diploma_year",
      council: "diploma_council", reg_number: "diploma_reg_number", reg_date: "diploma_reg_date", category: null, flag: "has_diploma",
    },
    DNB: {
      course: "dnb_title", college: "dnb_institute", year: "dnb_complete_year",
      council: "dnb_council", reg_number: "dnb_reg_number", reg_date: "dnb_reg_date", category: null, flag: "has_dnb",
    },
    PhD: {
      course: "phd_title", college: "phd_college", year: "phd_complete_year",
      council: "phd_council", reg_number: "phd_reg_number", reg_date: "phd_reg_date", category: "phd_category", flag: "has_phd",
    },
    "Super-Specialty": {
      course: "ss_title", college: "ss_college", year: "ss_complete_year",
      council: "ss_council", reg_number: "ss_reg_number", reg_date: "ss_reg_date", category: "ss_category", flag: "has_ss",
    },
    SS: {
      course: "ss_title", college: "ss_college", year: "ss_complete_year",
      council: "ss_council", reg_number: "ss_reg_number", reg_date: "ss_reg_date", category: "ss_category", flag: "has_ss",
    },
  };
  // Case-insensitive aliases
  const resolveQualLevel = (level) => {
    if (!level) return null;
    const canon = {ug:"UG",pg:"PG",diploma:"Diploma",dnb:"DNB",phd:"PhD",ss:"SS","super-specialty":"Super-Specialty","super specialty":"Super-Specialty","superspecialty":"SS","dm":"SS","mch":"SS"};
    return QUAL_LEVEL_MAP[level] || QUAL_LEVEL_MAP[canon[level.toLowerCase()]] || null;
  };

  const QUAL_CSV_HEADERS = {
    "s no": "sno", "s no.": "sno", "sno": "sno",
    "aadhaar": "aadhaar", "aadhaar #": "aadhaar", "aadhaar number": "aadhaar",
    "faculty name": "name", "name": "name",
    "current designation": "designation", "designation": "designation",
    "current department": "department", "department": "department",
    "degree level": "degree_level", "level": "degree_level",
    "degree type": "degree_type", "degree type (medical / dentistry / other)": "degree_type",
    "degree": "degree", "degree (mbbs / msc / bsc...)": "degree",
    "college": "college",
    "year of completion": "year", "year": "year", "completion year": "year",
    "council of registration": "council", "council": "council",
    "registration number": "reg_number", "reg number": "reg_number", "reg no": "reg_number",
    "date of registration": "reg_date", "reg date": "reg_date",
  };

  const parseQualCsv = (file) => {
    setQualFile(file.name);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      const rawHeaders = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
      const fieldMap = {};
      rawHeaders.forEach((h, i) => {
        const key = QUAL_CSV_HEADERS[h.toLowerCase().replace(/\s+/g, " ").trim()];
        if (key) fieldMap[i] = key;
      });

      // Parse rows
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = [];
        let cur = "", inQ = false;
        for (const ch of lines[i]) {
          if (ch === '"') inQ = !inQ;
          else if (ch === ',' && !inQ) { cells.push(cur.trim()); cur = ""; }
          else cur += ch;
        }
        cells.push(cur.trim());
        const rec = {};
        Object.entries(fieldMap).forEach(([idx, field]) => {
          rec[field] = (cells[Number(idx)] || "").replace(/^"|"$/g, "").trim();
        });
        if (rec.name || rec.aadhaar) rows.push(rec);
      }

      // Match to existing faculty (use match-index for unmasked aadhaar)
      let existing = [];
      try { const { data } = await api.get("/faculty/match-index"); existing = data; } catch {
        try { const { data } = await api.get("/faculty"); existing = data; } catch {}
      }
      const aadhaarLookup = {};
      const nameLookup = {};
      existing.forEach(f => {
        if (f.aadhaar) aadhaarLookup[f.aadhaar.replace(/\s/g, "")] = f;
        nameLookup[(f.full_name || "").toLowerCase().trim()] = f;
        if (f.title) nameLookup[`${f.title} ${f.full_name}`.toLowerCase().trim()] = f;
      });

      // Group by faculty (Aadhaar primary)
      const groupMap = {};
      rows.forEach(r => {
        const aadhaar = (r.aadhaar || "").replace(/\s/g, "");
        const key = aadhaar || (r.name || "").toLowerCase().trim();
        if (!key) return;
        if (!groupMap[key]) {
          const match = (aadhaar && aadhaarLookup[aadhaar]) || nameLookup[(r.name || "").toLowerCase().trim()] || null;
          groupMap[key] = { name: r.name, aadhaar, match, quals: [] };
        }
        const level = (r.degree_level || "").trim();
        const mapping = resolveQualLevel(level);
        if (!mapping) return; // Unknown level, skip
        // Build fields to set
        const fields = {};
        if (mapping.course && r.degree) fields[mapping.course] = r.degree;
        if (mapping.college && r.college) fields[mapping.college] = r.college;
        if (mapping.year && r.year) fields[mapping.year] = r.year;
        if (mapping.council && r.council) fields[mapping.council] = r.council;
        if (mapping.reg_number && r.reg_number) fields[mapping.reg_number] = r.reg_number;
        if (mapping.reg_date && r.reg_date) {
          const d = toISODate(r.reg_date);
          fields[mapping.reg_date] = d || r.reg_date;
        }
        if (mapping.category && r.degree_type) fields[mapping.category] = r.degree_type;
        if (mapping.flag) fields[mapping.flag] = true;

        // Detect conflicts with existing data
        const conflicts = {};
        if (groupMap[key].match) {
          const fac = groupMap[key].match;
          Object.entries(fields).forEach(([k, v]) => {
            const existing = fac[k];
            if (existing && String(existing).trim() && String(existing).trim() !== String(v).trim()) {
              conflicts[k] = existing;
            }
          });
        }
        groupMap[key].quals.push({ level, degree: r.degree, fields, conflicts });
      });

      setQualGroups(Object.values(groupMap));
      setQualDecisions({});
      setQualStep("preview");
    };
    reader.readAsText(file);
  };

  const executeQualImport = async () => {
    setQualImporting(true);
    let updated = 0, skipped = 0, errors = [];
    const batchId = `qual-batch-${Date.now()}`;
    const snapshots = [];

    for (let gi = 0; gi < qualGroups.length; gi++) {
      const g = qualGroups[gi];
      if (!g.match) { skipped++; continue; }
      try {
        // Snapshot existing qualification fields before update
        const origFields = {};
        const updateFields = {};
        for (const q of g.quals) {
          for (const [field, val] of Object.entries(q.fields)) {
            origFields[field] = g.match[field] || "";
            const decKey = `${gi}-${field}`;
            if (q.conflicts[field] && qualDecisions[decKey] === "keep") continue; // User chose to keep existing
            updateFields[field] = val;
          }
        }
        if (Object.keys(updateFields).length === 0) { skipped++; continue; }
        snapshots.push({ faculty_id: g.match.faculty_id, name: g.name, original_fields: origFields });
        await api.patch(`/faculty/${g.match.faculty_id}`, updateFields);
        updated++;
      } catch (e) {
        errors.push({ name: g.name, error: e?.response?.data?.detail || "Failed" });
      }
    }
    try {
      await api.post("/bulk/batch", {
        batch_id: batchId, batch_type: "qualifications",
        file_name: qualFile, total: qualGroups.length,
        created: 0, updated, skipped, error_count: errors.length,
        faculty_ids: [], snapshots,
      });
    } catch {}
    setQualResult({ updated, skipped, errors, total: qualGroups.length, batchId });
    setQualStep("result");
    setQualImporting(false);
    loadBatches();
  };

  const downloadQualTemplate = () => {
    const lines = [
      "S No.,Aadhaar #,Faculty Name,Current Designation,Current Department,Degree Level,Degree Type,Degree,College,Year of Completion,Council of Registration,Registration Number,Date of Registration",
      '1,234567890123,Rajesh Kumar Sharma,Associate Professor,General Medicine,UG,Medical,MBBS,"GMC Jabalpur",1998,MP Medical Council,MC-12345,"Jun 15, 2000"',
      '2,234567890123,Rajesh Kumar Sharma,Associate Professor,General Medicine,PG,Medical,MD General Medicine,"AIIMS Bhopal",2004,NMC,NMC-67890,"Jan 10, 2005"',
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "SSMCH_Qualifications_Import_Template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ═══════ AEBAS ATTENDANCE IMPORT ═══════

  // AEBAS ID Mapping (dedicated server-side upload)
  const [aebasMappingStep, setAebasMappingStep] = useState(null); // null | preview | importing | result
  const [aebasMappingRecords, setAebasMappingRecords] = useState([]);
  const [aebasMappingResult, setAebasMappingResult] = useState(null);
  const aebasMappingRef = useRef(null);

  const parseAebasMappingCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());
      const aadhaarIdx = headers.findIndex(h => h.includes("aadhaar"));
      const aebasIdx = headers.findIndex(h => h.includes("aebas"));
      if (aadhaarIdx === -1 || aebasIdx === -1) { alert("CSV must have Aadhaar and AEBAS ID columns"); return; }
      const records = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split(",").map(c => c.trim().replace(/^"|"$/g, "").trim());
        const aadhaar = (cells[aadhaarIdx] || "").trim();
        const aebas_id = (cells[aebasIdx] || "").trim();
        if (aadhaar && aebas_id) records.push({ aadhaar, aebas_id });
      }
      setAebasMappingRecords(records);
      setAebasMappingStep("preview");
    };
    reader.readAsText(file);
  };

  const executeAebasMapping = async () => {
    setAebasMappingStep("importing");
    try {
      const { data } = await api.post("/bulk/aebas-mapping", { pin, records: aebasMappingRecords });
      setAebasMappingResult(data);
      setAebasMappingStep("result");
    } catch (e) {
      setAebasMappingResult({ error: e?.response?.data?.detail || "Failed" });
      setAebasMappingStep("result");
    }
  };

  // ══════ Aadhaar Restore functions ══════
  const parseRestoreCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").replace(/^="?|"?$/g, "").toLowerCase());
      const nameIdx = headers.findIndex(h => h.includes("name") || h.includes("faculty"));
      const aadhaarIdx = headers.findIndex(h => h.includes("aadhaar"));
      if (nameIdx === -1 || aadhaarIdx === -1) { alert("CSV must have a Name/Faculty column and an Aadhaar column"); return; }
      const records = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split(",").map(c => c.trim().replace(/^"|"$/g, "").replace(/^="?|"?$/g, "").trim());
        const name = (cells[nameIdx] || "").trim();
        let aadhaar = (cells[aadhaarIdx] || "").replace(/\s/g, "").replace(/^='?"?|"?'?$/g, "");
        if (name && aadhaar && /^\d{12}$/.test(aadhaar)) records.push({ name, aadhaar });
      }
      if (records.length === 0) { alert("No valid records found. Each row needs a name and a 12-digit Aadhaar number."); return; }
      setRestoreRecords(records);
      setRestoreStep("preview");
    };
    reader.readAsText(file);
  };

  const executeRestore = async () => {
    setRestoreStep("importing");
    try {
      const { data } = await api.post("/bulk/restore-aadhaar", { pin, records: restoreRecords });
      setRestoreResult(data);
      setRestoreStep("result");
    } catch (e) {
      setRestoreResult({ error: e?.response?.data?.detail || "Restore failed" });
      setRestoreStep("result");
    }
  };

  const AEBAS_CSV_HEADERS = {
    "s.no": "sno", "sno": "sno",
    "attendance id": "attendance_id", "attendanceid": "attendance_id",
    "user name": "user_name", "username": "user_name", "name": "user_name",
    "user type": "user_type",
    "user's designation": "designation", "designation": "designation",
    "office locations": "office_location", "office location": "office_location",
    "division/units": "department", "division": "department", "units": "department",
    "in time": "in_time", "intime": "in_time",
    "out time": "out_time", "outtime": "out_time",
    "status": "status",
  };

  const parseAebasCsv = (file) => {
    setAebasFile(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      let text = e.target.result;
      // Fix Excel formula export: ="""VALUE" → VALUE (preserves the numeric ID)
      text = text.replace(/="{3}(\d+)"/g, '$1"');
      text = text.replace(/="{2}(\d+)"{3}/g, '$1"');
      text = text.replace(/=""([^"]+)""/g, '$1');

      const parseCSVLine = (line) => {
        const cells = [];
        let cur = "", inQ = false, i = 0;
        while (i < line.length) {
          const ch = line[i];
          if (inQ) {
            if (ch === '"') {
              if (i + 1 < line.length && line[i + 1] === '"') { cur += '"'; i += 2; }
              else { inQ = false; i++; }
            } else { cur += ch; i++; }
          } else {
            if (ch === '"') { inQ = true; i++; }
            else if (ch === ',') { cells.push(cur.trim()); cur = ""; i++; }
            else { cur += ch; i++; }
          }
        }
        cells.push(cur.trim());
        return cells;
      };

      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      const headerCells = parseCSVLine(lines[0]);
      const fieldMap = {};
      headerCells.forEach((h, i) => {
        const key = AEBAS_CSV_HEADERS[h.toLowerCase().replace(/\s+/g, " ").trim()];
        if (key) fieldMap[i] = key;
      });
      const records = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = parseCSVLine(lines[i]);
        const rec = {};
        Object.entries(fieldMap).forEach(([idx, field]) => {
          rec[field] = (cells[Number(idx)] || "").trim();
        });
        if (rec.user_name) records.push(rec);
      }
      setAebasRecords(records);
      setAebasStep("preview");
    };
    reader.readAsText(file);
  };

  const executeAebasImport = async () => {
    setAebasImporting(true);
    try {
      const { data } = await api.post("/bulk/aebas", { records: aebasRecords });
      setAebasResult(data);
      setAebasStep("result");
    } catch (e) {
      setAebasResult({ error: e?.response?.data?.detail || "Import failed" });
      setAebasStep("result");
    }
    setAebasImporting(false);
  };

  // Download model CSV template
  const downloadTemplate = () => {
    const headers = [];
    const sampleRow = [];
    const formatRow = [];
    
    const samples = {
      title: "Dr.", full_name: "Rajesh Kumar Sharma", dob: "Mar 15, 1975", blood_group: "B+",
      gender: "Male", marital_status: "Married", aadhaar: "234567890123", pan: "ABCDE1234F",
      whatsapp: "9876543210", personal_email: "rajesh.sharma@gmail.com", official_email: "rajesh.sharma@ssmch.in",
      corr_house: "Flat 12, Tower B, MG Road", corr_area: "Wright Town", corr_city: "Jabalpur",
      corr_state: "Madhya Pradesh", corr_pin: "482001",
      emergency_contact_name: "Priya Sharma", emergency_contact_relation: "Spouse", emergency_contact_phone: "9876543211",
      department: "General Medicine", designation: "Associate Professor", joined_at: "Jul 01, 2018",
      employment_type: "Regular", track: "Teaching", status: "Active",
      ifsc_code: "SBIN0001234", bank_name: "State Bank of India", bank_branch: "Jabalpur Main",
      account_number: "30281234567", monthly_salary: "185000", greyt_hr_id: "SSMCH-0456",
      aebas_id: "AEB-9012", id_card_status: "Issued",
      ug_course: "MBBS", ug_college: "GMC Jabalpur", ug_complete_year: "1998",
      ug_council: "MP Medical Council", ug_reg_date: "Jan 15, 1999",
      pg_category: "Medical", pg_title: "MD General Medicine", pg_college: "AIIMS Bhopal",
      pg_complete_year: "2003", pg_council: "MP Medical Council", pg_reg_date: "Jun 20, 2003",
      publications: "12", met_done: "true", bmr_done: "true",
      post_pg_years: "22", years_in_role: "6", age: "50",
    };
    
    const formats = {
      title: "Dr./Mr./Ms./Mrs.", full_name: "Text", dob: "Mon DD, YYYY (e.g. Mar 15, 1975)", blood_group: "A+/A-/B+/B-/AB+/AB-/O+/O-",
      gender: "Male/Female/Other", marital_status: "Single/Married/Widowed/Divorced", aadhaar: "12 digits",
      pan: "ABCDE1234F (5 letters, 4 digits, 1 letter)", whatsapp: "10 digits", personal_email: "email@domain.com",
      official_email: "email@ssmch.in",
      corr_house: "Text", corr_area: "Text", corr_city: "Text", corr_state: "Indian State Name", corr_pin: "6 digits",
      emergency_contact_name: "Text", emergency_contact_relation: "Spouse/Parent/Sibling/Other", emergency_contact_phone: "10 digits",
      department: "NMC Department Name", designation: "Professor/Associate Professor/Assistant Professor/Senior Resident/Tutor",
      joined_at: "Mon DD, YYYY (e.g. Jul 01, 2018)", employment_type: "Regular/Visiting Local/Visiting Non-Local/Consultant Recruited",
      track: "Teaching/Non-Teaching", status: "Active/Absconded/Relieved/Archived",
      ifsc_code: "11 chars: 4 letters + 0 + 6 alphanumeric", bank_name: "Text", bank_branch: "Text",
      account_number: "5-20 digits", monthly_salary: "Number (no commas)", greyt_hr_id: "Text", aebas_id: "Text",
      id_card_status: "Pending/Issued/Returned",
      ug_course: "MBBS/BDS/BSc/Other", ug_college: "NMC College Name", ug_complete_year: "YYYY",
      ug_council: "State Medical Council Name", ug_reg_date: "Mon DD, YYYY",
      pg_category: "Medical/Dentistry/Others", pg_title: "MD/MS/DNB + Specialty", pg_college: "NMC College Name",
      pg_complete_year: "YYYY", pg_council: "State Medical Council Name", pg_reg_date: "Mon DD, YYYY",
      publications: "Integer", met_done: "true/false", bmr_done: "true/false",
      post_pg_years: "Number", years_in_role: "Number", age: "Integer (18-100)",
    };

    FIELD_GROUPS.forEach(g => {
      g.fields.forEach(f => {
        headers.push(f.label);
        sampleRow.push(samples[f.key] || "");
        formatRow.push(formats[f.key] || "Text");
      });
    });

    const escape = (v) => {
      const s = String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    
    const lines = [
      headers.map(escape).join(","),
      formatRow.map(v => escape("FORMAT: " + v)).join(","),
      sampleRow.map(escape).join(","),
    ];
    
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "SSMCH_FIMS_Import_Template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Mapped field names for display
  const mappedFieldNames = useMemo(() => {
    const m = {};
    Object.values(mapping).forEach(fKey => {
      const f = ALL_FIELDS.find(x => x.key === fKey);
      if (f) m[fKey] = f.label;
    });
    return m;
  }, [mapping]);

  const activeMappings = Object.entries(mapping).filter(([, v]) => v);
  const hasAadhaar = activeMappings.some(([, v]) => v === "aadhaar");
  const newRows = previewData.filter(r => r.isNew && !r.skip);
  const updateRows = previewData.filter(r => !r.isNew && !r.skip);
  const conflictRows = previewData.filter(r => Object.keys(r.conflicts).length > 0 && !r.skip);

  // ===== RENDER =====

  if (!pinUnlocked) {
    return (
      <div data-testid="view-bulk-import">
        <div className="topbar"><div><h1 className="pg-title">Bulk CSV Import</h1><div className="pg-sub">PIN-protected · staged data import via CSV</div></div></div>
        <div style={{maxWidth:400,margin:'60px auto',textAlign:'center'}}>
          <div style={{fontSize:14,fontWeight:500,marginBottom:12}}>Enter PIN to access bulk import</div>
          <input
            type="password" className="form-input mono" placeholder="Enter 4-digit PIN"
            maxLength={4} autoFocus value={pin}
            onChange={e => { setPin(e.target.value.replace(/[^0-9]/g,"")); setPinErr(""); }}
            onKeyDown={e => { if (e.key === "Enter") verifyPin(); }}
            style={{textAlign:"center",fontSize:20,letterSpacing:8,marginBottom:8}}
            data-testid="bulk-pin-input"
          />
          {pinErr && <div style={{color:"#dc2626",fontSize:12,marginBottom:8}} data-testid="bulk-pin-error">{pinErr}</div>}
          <button className="btn primary" disabled={pin.length < 4} onClick={verifyPin} data-testid="bulk-pin-submit">Unlock</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="view-bulk-import">
      <div className="topbar">
        <div>
          <h1 className="pg-title">Bulk Import</h1>
          <div className="pg-sub">
            {mode === "faculty" && step === "upload" && "Faculty Data · Upload CSV with any subset of fields"}
            {mode === "faculty" && step === "map" && `Faculty Data · Map columns · ${csvRows.length} rows · ${csvHeaders.length} columns`}
            {mode === "faculty" && step === "preview" && `Faculty Data · Preview · ${newRows.length} new · ${updateRows.length} updates · ${conflictRows.length} conflicts`}
            {mode === "faculty" && step === "result" && "Faculty Data · Import complete"}
            {mode === "experience" && expStep === "upload" && "Past Experience · Upload career journey CSV"}
            {mode === "experience" && expStep === "preview" && `Past Experience · ${expGroups.length} faculty · ${expGroups.reduce((a,g)=>a+g.pastPositions.length,0)} positions`}
            {mode === "experience" && expStep === "result" && "Past Experience · Import complete"}
            {mode === "nmc" && nmcStep === "upload" && `NMC Courses & Colleges · ${nmcStats ? `${nmcStats.total} courses · ${nmcStats.unique_colleges || '—'} colleges loaded` : 'Upload recognized courses CSV'}`}
            {mode === "nmc" && nmcStep === "preview" && `NMC Courses & Colleges · Preview · ${nmcRecords.length} course records parsed`}
            {mode === "nmc" && nmcStep === "result" && "NMC Courses & Colleges · Import complete"}
            {mode === "qualifications" && qualStep === "upload" && "Qualifications · Upload educational qualification CSV"}
            {mode === "qualifications" && qualStep === "preview" && `Qualifications · Preview · ${qualGroups.length} faculty · ${qualGroups.reduce((a,g)=>a+g.quals.length,0)} qualifications`}
            {mode === "qualifications" && qualStep === "result" && "Qualifications · Import complete"}
            {mode === "aebas" && aebasStep === "upload" && "AEBAS Attendance · Upload NMC portal attendance CSV"}
            {mode === "aebas" && aebasStep === "preview" && `AEBAS Attendance · Preview · ${aebasRecords.length} records`}
            {mode === "aebas" && aebasStep === "result" && "AEBAS Attendance · Import complete"}
            {mode === "restore" && restoreStep === "upload" && "Aadhaar Restore · Upload CSV to fix corrupted Aadhaar data"}
            {mode === "restore" && restoreStep === "preview" && `Aadhaar Restore · Preview · ${restoreRecords.length} records`}
            {mode === "restore" && restoreStep === "result" && "Aadhaar Restore · Complete"}
          </div>
        </div>
        {((mode === "faculty" && step !== "upload" && step !== "result") || (mode === "experience" && expStep !== "upload" && expStep !== "result") || (mode === "nmc" && nmcStep !== "upload" && nmcStep !== "result") || (mode === "qualifications" && qualStep !== "upload" && qualStep !== "result") || (mode === "aebas" && aebasStep !== "upload" && aebasStep !== "result") || (mode === "restore" && restoreStep !== "upload" && restoreStep !== "result")) && (
          <button className="btn" onClick={() => { if (mode === "faculty") { setStep("upload"); setCsvHeaders([]); setCsvRows([]); setPreviewData([]); setMapping({}); } else if (mode === "experience") { setExpStep("upload"); setExpGroups([]); } else if (mode === "nmc") { setNmcStep("upload"); setNmcRecords([]); } else if (mode === "aebas") { setAebasStep("upload"); setAebasRecords([]); } else if (mode === "restore") { setRestoreStep("upload"); setRestoreRecords([]); } else { setQualStep("upload"); setQualGroups([]); } }} data-testid="bulk-restart">Start Over</button>
        )}
      </div>

      {/* Mode Tabs */}
      <div style={{display:"flex",gap:0,marginBottom:18,borderBottom:"2px solid var(--border)"}}>
        {[{key:"faculty",label:"Faculty Data"},{key:"experience",label:"Past Experience"},{key:"qualifications",label:"Qualifications"},{key:"aebas",label:"AEBAS Attendance"},{key:"nmc",label:"NMC Colleges"},{key:"restore",label:"Aadhaar Restore"}].map(t => (
          <button key={t.key} onClick={() => setMode(t.key)}
            style={{padding:"10px 24px",fontSize:13,fontWeight:mode===t.key?600:400,border:"none",borderBottom:mode===t.key?"2px solid var(--accent)":"2px solid transparent",marginBottom:-2,cursor:"pointer",
              background:"transparent",color:mode===t.key?"var(--accent)":"var(--text-2)"}}
            data-testid={`tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      {/* STEP 1: Upload */}
      {mode === "faculty" && step === "upload" && (
        <div style={{maxWidth:560,margin:"40px auto",textAlign:"center"}}>
          <div style={{marginBottom:18,display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <button className="btn primary" onClick={downloadTemplate} data-testid="bulk-download-template" style={{fontSize:13,padding:"10px 24px"}}>
              Download Import Template
            </button>
            <button className="btn" onClick={async () => { try { const resp = await api.get("/bulk/export-faculty", { responseType: "blob" }); const url = URL.createObjectURL(new Blob([resp.data])); const a = document.createElement("a"); a.href = url; a.download = "SSMCH_Faculty_Export.csv"; a.click(); URL.revokeObjectURL(url); } catch { alert("Export failed"); } }} data-testid="bulk-export-faculty" style={{fontSize:13,padding:"10px 24px"}}>
              Export All Faculty Data
            </button>
            <div style={{fontSize:11,color:"var(--text-3)",marginTop:6}}>All fields with expected formats and a sample row</div>
          </div>
          <div
            style={{border:"2px dashed var(--border)",borderRadius:14,padding:"48px 24px",cursor:"pointer",marginBottom:16}}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
            data-testid="bulk-dropzone"
          >
            <div style={{fontSize:32,marginBottom:8,color:"var(--text-3)"}}>CSV</div>
            <div style={{fontSize:14,fontWeight:500}}>Drop CSV file here or click to browse</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginTop:6}}>Supports .csv files exported from Google Sheets or Excel</div>
          </div>
          <input ref={fileRef} type="file" accept=".csv" style={{display:"none"}} onChange={e => handleFile(e.target.files[0])} data-testid="bulk-file-input"/>
          <div style={{background:"rgba(107,156,255,0.06)",border:"1px solid rgba(107,156,255,0.2)",borderRadius:8,padding:"12px 16px",fontSize:12,color:"var(--text-2)",textAlign:"left"}}>
            <strong>How it works:</strong>
            <ol style={{margin:"6px 0 0 16px",padding:0,lineHeight:1.8}}>
              <li>Upload a CSV with any subset of faculty fields</li>
              <li>Map CSV columns to system fields (auto-detected where possible)</li>
              <li>Preview matches — rows matched by <strong>Aadhaar number</strong> update existing records; unmatched rows create new faculty</li>
              <li>Review conflicts per-row and choose CSV or existing value</li>
            </ol>
          </div>
          {/* Batch History */}
          {batches.length > 0 && (
            <div style={{marginTop:30}}>
              <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1,color:"var(--text-3)",fontWeight:600,marginBottom:10}}>Import History</div>
              {batches.map(b => (
                <div key={b.batch_id} style={{border:"1px solid var(--border)",borderRadius:10,padding:"12px 16px",marginBottom:8,opacity:b.deleted?0.4:1}} data-testid={`batch-${b.batch_id}`}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontWeight:500,fontSize:13}}>{b.file_name} {b.deleted && <span style={{color:"#dc2626",fontSize:11}}>(deleted)</span>}</div>
                      <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>
                        {new Date(b.created_at).toLocaleString()} · {b.created_by}
                      </div>
                      <div style={{fontSize:11,color:"var(--text-2)",marginTop:2}}>
                        <span style={{color:"#059669"}}>{b.created} created</span>
                        {b.updated > 0 && <span> · <span style={{color:"#2563eb"}}>{b.updated} updated</span></span>}
                        {b.skipped > 0 && <span> · {b.skipped} skipped</span>}
                        {b.error_count > 0 && <span> · <span style={{color:"#dc2626"}}>{b.error_count} errors</span></span>}
                        {b.deleted && <span> · {b.deleted_count} records removed</span>}
                      </div>
                    </div>
                    {!b.deleted && b.created > 0 && (
                      <button className="btn" style={{fontSize:11,padding:"4px 12px",color:"#dc2626",flexShrink:0}}
                        onClick={() => { setUndoTarget(b); setUndoPin(""); setUndoErr(""); }}
                        data-testid={`undo-${b.batch_id}`}
                      >Undo Batch</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Undo Batch Modal */}
      {undoTarget && (
        <div style={{position:"fixed",inset:0,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.4)"}} onClick={() => setUndoTarget(null)} data-testid="undo-modal-overlay">
          <div style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:14,padding:"28px 32px",minWidth:360,maxWidth:440,boxShadow:"0 16px 48px rgba(0,0,0,.15)"}} onClick={e => e.stopPropagation()} data-testid="undo-modal">
            <div style={{fontSize:16,fontWeight:600,marginBottom:4,color:"#dc2626"}}>
              {undoTarget.batch_type === "experience" ? "Undo Experience Import" : undoTarget.batch_type === "qualifications" ? "Undo Qualifications Import" : "Undo Batch Import"}
            </div>
            <div style={{fontSize:13,color:"var(--text-2)",marginBottom:4}}>
              {undoTarget.batch_type === "experience"
                ? <>This will <strong>revert past experience</strong> for <strong>{undoTarget.updated} faculty</strong> to their state before this import.</>
                : undoTarget.batch_type === "qualifications"
                ? <>This will <strong>revert qualification fields</strong> for <strong>{undoTarget.updated} faculty</strong> to their state before this import.</>
                : <>This will <strong>permanently delete {undoTarget.created} faculty record{undoTarget.created !== 1 ? "s" : ""}</strong> created by:</>
              }
            </div>
            <div style={{fontSize:12,background:"var(--bg-alt)",borderRadius:6,padding:"8px 12px",marginBottom:14}}>
              <div style={{fontWeight:500}}>{undoTarget.file_name}</div>
              <div style={{color:"var(--text-3)",fontSize:11}}>{new Date(undoTarget.created_at).toLocaleString()}</div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:12,fontWeight:500,display:"block",marginBottom:4}}>Enter PIN to confirm</label>
              <input type="password" className="form-input mono" placeholder="Enter 4-digit PIN" maxLength={4} autoFocus
                value={undoPin} onChange={e => { setUndoPin(e.target.value.replace(/[^0-9]/g,"")); setUndoErr(""); }}
                onKeyDown={e => { if (e.key === "Enter" && undoPin.length >= 4) executeUndo(); }}
                style={{textAlign:"center",fontSize:18,letterSpacing:8}} data-testid="undo-pin-input"/>
            </div>
            {undoErr && <div style={{color:"#dc2626",fontSize:12,marginBottom:8}} data-testid="undo-pin-error">{undoErr}</div>}
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button className="btn" style={{fontSize:12}} onClick={() => setUndoTarget(null)}>Cancel</button>
              <button className="btn" style={{fontSize:12,padding:"6px 18px",background:"#dc2626",color:"#fff",border:"none",borderRadius:6}}
                disabled={undoLoading || undoPin.length < 4} onClick={executeUndo} data-testid="undo-confirm">
                {undoLoading ? "Processing…" : undoTarget.batch_type === "experience" ? `Revert ${undoTarget.updated} faculty` : `Delete ${undoTarget.created} records`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: Column Mapping */}
      {mode === "faculty" && step === "map" && (
        <div style={{maxWidth:720,margin:"20px auto"}}>
          <div className="form-section" style={{padding:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div>
                <div style={{fontWeight:500,fontSize:14}}>{fileName}</div>
                <div style={{fontSize:12,color:"var(--text-3)"}}>{csvRows.length} rows · {csvHeaders.length} columns</div>
              </div>
              <div style={{fontSize:12,color: hasAadhaar ? "#059669" : "#dc2626",fontWeight:500}}>
                {hasAadhaar ? "Aadhaar column mapped" : "Map an Aadhaar column to match existing faculty"}
              </div>
            </div>

            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{borderBottom:"2px solid var(--border)"}}>
                  <th style={{textAlign:"left",padding:"8px 6px",fontWeight:600}}>CSV Column</th>
                  <th style={{textAlign:"left",padding:"8px 6px",fontWeight:600}}>Sample Values</th>
                  <th style={{textAlign:"left",padding:"8px 6px",fontWeight:600}}>Map to Field</th>
                </tr>
              </thead>
              <tbody>
                {csvHeaders.map(h => {
                  const samples = csvRows.slice(0, 3).map(r => r[h]).filter(Boolean).join(", ");
                  return (
                    <tr key={h} style={{borderBottom:"1px solid var(--border)"}}>
                      <td style={{padding:"8px 6px",fontWeight:500}} className="mono">{h}</td>
                      <td style={{padding:"8px 6px",color:"var(--text-3)",fontSize:11,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{samples || "—"}</td>
                      <td style={{padding:"8px 6px"}}>
                        <select
                          className="form-select"
                          style={{fontSize:12,padding:"4px 8px"}}
                          value={mapping[h] || ""}
                          onChange={e => setMapping(prev => ({ ...prev, [h]: e.target.value }))}
                          data-testid={`map-${h.replace(/\s/g,"-")}`}
                        >
                          <option value="">— Skip this column —</option>
                          {FIELD_GROUPS.map(g => (
                            <optgroup key={g.group} label={g.group}>
                              {g.fields.map(f => (
                                <option key={f.key} value={f.key} disabled={Object.values(mapping).includes(f.key) && mapping[h] !== f.key}>
                                  {f.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{display:"flex",justifyContent:"space-between",marginTop:16}}>
            <button className="btn" onClick={() => setStep("upload")}>Back</button>
            <button className="btn primary" disabled={activeMappings.length === 0} onClick={buildPreview} data-testid="bulk-preview-btn">
              Preview Import ({activeMappings.length} field{activeMappings.length !== 1 ? "s" : ""} mapped)
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Preview */}
      {mode === "faculty" && step === "preview" && (
        <div style={{margin:"20px auto"}}>
          {/* Summary chips */}
          <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
            <div style={{padding:"8px 16px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)",fontSize:13}}>
              <strong style={{color:"#059669"}}>{newRows.length}</strong> new faculty to create
            </div>
            <div style={{padding:"8px 16px",borderRadius:8,background:"rgba(107,156,255,0.06)",border:"1px solid rgba(107,156,255,0.2)",fontSize:13}}>
              <strong style={{color:"#2563eb"}}>{updateRows.length}</strong> existing to update
            </div>
            {conflictRows.length > 0 && (
              <div style={{padding:"8px 16px",borderRadius:8,background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",fontSize:13}}>
                <strong style={{color:"#b45309"}}>{conflictRows.length}</strong> with conflicts (review below)
              </div>
            )}
            {(() => { const errCount = previewData.filter(r => !r.skip && Object.keys(r.errors).length > 0).length; return errCount > 0 ? (
              <div style={{padding:"8px 16px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)",fontSize:13}}>
                <strong style={{color:"#dc2626"}}>{errCount}</strong> row{errCount>1?"s":""} with validation errors
              </div>
            ) : null; })()}
          </div>

          {/* Preview table */}
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{borderBottom:"2px solid var(--border)",background:"var(--bg-alt)"}}>
                  <th style={{padding:"8px 6px",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>Row</th>
                  <th style={{padding:"8px 6px",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>Status</th>
                  <th style={{padding:"8px 6px",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>Aadhaar</th>
                  <th style={{padding:"8px 6px",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>Name</th>
                  {activeMappings.filter(([,v]) => v !== "aadhaar" && v !== "full_name").slice(0, 5).map(([,fKey]) => (
                    <th key={fKey} style={{padding:"8px 6px",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{mappedFieldNames[fKey] || fKey}</th>
                  ))}
                  <th style={{padding:"8px 6px",textAlign:"center",fontWeight:600}}>Action</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, idx) => {
                  const hasConflict = Object.keys(row.conflicts).length > 0;
                  const hasErrors = Object.keys(row.errors).length > 0;
                  const rowBg = row.skip ? "rgba(0,0,0,0.03)" : hasErrors ? "rgba(248,113,113,0.05)" : row.isNew ? "rgba(52,211,153,0.03)" : hasConflict ? "rgba(251,191,36,0.04)" : "transparent";
                  return (
                    <tr key={idx} style={{borderBottom:"1px solid var(--border)",background:rowBg,opacity:row.skip?0.4:1}} data-testid={`preview-row-${idx}`}>
                      <td style={{padding:"8px 6px"}} className="mono">{idx + 1}</td>
                      <td style={{padding:"8px 6px"}}>
                        {row.skip ? <span style={{color:"var(--text-3)"}}>Skipped</span>
                          : hasErrors ? <span style={{color:"#dc2626",fontWeight:500}} title={Object.values(row.errors).join("; ")}>Error ({Object.keys(row.errors).length})</span>
                          : row.isNew ? <span style={{color:"#059669",fontWeight:500}}>New</span>
                          : hasConflict ? <span style={{color:"#b45309",fontWeight:500}}>Conflict</span>
                          : <span style={{color:"#2563eb",fontWeight:500}}>Update</span>}
                      </td>
                      <td style={{padding:"8px 6px",background:row.errors.aadhaar?"rgba(248,113,113,0.1)":"transparent"}} className="mono">
                        {row.aadhaar ? `…${row.aadhaar.slice(-4)}` : "—"}
                        {row.errors.aadhaar && <div style={{fontSize:9,color:"#dc2626",whiteSpace:"normal"}}>{row.errors.aadhaar}</div>}
                      </td>
                      <td style={{padding:"8px 6px",fontWeight:500}}>{row.mappedFields.full_name || row.matchedFaculty?.full_name || "—"}</td>
                      {activeMappings.filter(([,v]) => v !== "aadhaar" && v !== "full_name").slice(0, 5).map(([,fKey]) => {
                        const cellErr = row.errors[fKey];
                        return (
                        <td key={fKey} style={{padding:"8px 6px",maxWidth:140,overflow:"hidden",background:cellErr?"rgba(248,113,113,0.1)":"transparent"}}>
                          {row.conflicts[fKey] ? (
                            <span style={{color:"#b45309"}} title={`Existing: ${row.conflicts[fKey].existing} | CSV: ${row.conflicts[fKey].csv}`}>
                              {String(row.mappedFields[fKey] || "")}
                            </span>
                          ) : (
                            <span style={{color:cellErr?"#dc2626":"inherit"}}>{String(row.mappedFields[fKey] ?? "")}</span>
                          )}
                          {cellErr && <div style={{fontSize:9,color:"#dc2626",whiteSpace:"normal",marginTop:2}} title={cellErr}>{cellErr}</div>}
                        </td>
                      );})}
                      <td style={{padding:"8px 6px",textAlign:"center"}}>
                        <button
                          className="btn"
                          style={{fontSize:10,padding:"2px 8px",color:row.skip?"#059669":"var(--text-3)"}}
                          onClick={() => {
                            const updated = [...previewData];
                            updated[idx] = { ...updated[idx], skip: !updated[idx].skip };
                            setPreviewData(updated);
                          }}
                          data-testid={`toggle-row-${idx}`}
                        >{row.skip ? "Include" : "Skip"}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Validation Errors */}
          {(() => {
            const errorRows = previewData.filter(r => !r.skip && Object.keys(r.errors).length > 0);
            if (errorRows.length === 0) return null;
            return (
              <div className="form-section" style={{padding:16,marginTop:16,borderColor:"rgba(248,113,113,0.3)"}}>
                <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1,color:"#dc2626",fontWeight:600,marginBottom:12}}>
                  Validation Errors ({errorRows.length} row{errorRows.length>1?"s":""})
                </div>
                <div style={{fontSize:12,color:"var(--text-2)",marginBottom:12}}>
                  Rows with errors will still be imported but the invalid values may cause issues. Fix them in the CSV or skip the rows.
                </div>
                {errorRows.map(row => (
                  <div key={row.rowIdx} style={{border:"1px solid rgba(248,113,113,0.2)",borderRadius:8,padding:12,marginBottom:8,background:"rgba(248,113,113,0.03)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <div style={{fontWeight:500,fontSize:13}}>
                        Row {row.rowIdx + 1} — {row.mappedFields.full_name || "Unknown"} — {Object.keys(row.errors).length} error{Object.keys(row.errors).length>1?"s":""}
                      </div>
                      <button className="btn" style={{fontSize:10,padding:"2px 8px",color:row.skip?"#059669":"#dc2626"}}
                        onClick={() => { const u = [...previewData]; u[row.rowIdx] = {...u[row.rowIdx], skip:!u[row.rowIdx].skip}; setPreviewData(u); }}
                      >{row.skip ? "Include" : "Skip row"}</button>
                    </div>
                    {Object.entries(row.errors).map(([fKey, errMsg]) => (
                      <div key={fKey} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"4px 0",borderTop:"1px solid rgba(248,113,113,0.1)",fontSize:12}}>
                        <span style={{color:"#dc2626",fontWeight:700,flexShrink:0}}>!</span>
                        <span style={{fontWeight:500,minWidth:100}}>{mappedFieldNames[fKey] || fKey}</span>
                        <span className="mono" style={{color:"#dc2626"}}>{String(row.mappedFields[fKey])}</span>
                        <span style={{color:"var(--text-3)",fontSize:11}}>— {errMsg}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Conflict Resolution */}
          {conflictRows.length > 0 && (
            <div className="form-section" style={{padding:16,marginTop:16}}>
              <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1,color:"var(--text-3)",fontWeight:600,marginBottom:12}}>Conflict Resolution ({conflictRows.length} row{conflictRows.length>1?"s":""})</div>
              {conflictRows.map(row => (
                <div key={row.rowIdx} style={{border:"1px solid rgba(251,191,36,0.3)",borderRadius:8,padding:12,marginBottom:10,background:"rgba(251,191,36,0.03)"}}>
                  <div style={{fontWeight:500,fontSize:13,marginBottom:8}}>
                    Row {row.rowIdx + 1} — {row.matchedFaculty?.title} {row.matchedFaculty?.full_name} ({row.matchedFaculty?.ssmch_id})
                  </div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{borderBottom:"1px solid var(--border)"}}>
                        <th style={{textAlign:"left",padding:"4px 6px"}}>Field</th>
                        <th style={{textAlign:"left",padding:"4px 6px"}}>Existing Value</th>
                        <th style={{textAlign:"left",padding:"4px 6px"}}>CSV Value</th>
                        <th style={{textAlign:"center",padding:"4px 6px"}}>Use</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(row.conflicts).map(([fKey, { existing, csv }]) => {
                        const decision = rowDecisions[row.rowIdx]?.[fKey] || "csv";
                        return (
                          <tr key={fKey} style={{borderBottom:"1px solid var(--border)"}}>
                            <td style={{padding:"6px",fontWeight:500}}>{mappedFieldNames[fKey] || fKey}</td>
                            <td style={{padding:"6px",color: decision === "keep" ? "#059669" : "var(--text-3)"}} className="mono">{String(existing)}</td>
                            <td style={{padding:"6px",color: decision === "csv" ? "#2563eb" : "var(--text-3)"}} className="mono">{String(csv)}</td>
                            <td style={{padding:"6px",textAlign:"center"}}>
                              <div style={{display:"flex",gap:0,border:"1px solid var(--border)",borderRadius:6,overflow:"hidden",justifyContent:"center"}}>
                                <button type="button" onClick={() => setRowDecisions(prev => ({...prev, [row.rowIdx]: {...(prev[row.rowIdx]||{}), [fKey]: "keep"}}))}
                                  style={{padding:"3px 10px",fontSize:10,border:"none",cursor:"pointer",fontWeight:decision==="keep"?600:400,
                                    background:decision==="keep"?"#059669":"var(--bg)",color:decision==="keep"?"#fff":"var(--text-2)"}}
                                  data-testid={`conflict-keep-${row.rowIdx}-${fKey}`}
                                >Keep</button>
                                <button type="button" onClick={() => setRowDecisions(prev => ({...prev, [row.rowIdx]: {...(prev[row.rowIdx]||{}), [fKey]: "csv"}}))}
                                  style={{padding:"3px 10px",fontSize:10,border:"none",cursor:"pointer",fontWeight:decision==="csv"?600:400,
                                    background:decision==="csv"?"#2563eb":"var(--bg)",color:decision==="csv"?"#fff":"var(--text-2)"}}
                                  data-testid={`conflict-csv-${row.rowIdx}-${fKey}`}
                                >CSV</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}

          <div style={{display:"flex",justifyContent:"space-between",marginTop:16}}>
            <button className="btn" onClick={() => setStep("map")}>Back to Mapping</button>
            <button
              className="btn primary"
              disabled={importing || (newRows.length === 0 && updateRows.length === 0)}
              onClick={executeImport}
              data-testid="bulk-execute-btn"
            >{importing ? "Importing…" : `Import ${newRows.length + updateRows.length} rows`}</button>
          </div>
        </div>
      )}

      {/* STEP 4: Result */}
      {mode === "faculty" && step === "result" && importResult && (
        <div style={{maxWidth:560,margin:"40px auto"}}>
          <div className="form-section" style={{padding:24,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>
              {importResult.errors.length === 0 ? "Import Complete" : "Import Complete (with errors)"}
            </div>
            <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:16,flexWrap:"wrap"}}>
              {importResult.created > 0 && (
                <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)"}}>
                  <div style={{fontSize:24,fontWeight:700,color:"#059669"}} className="mono">{importResult.created}</div>
                  <div style={{fontSize:12,color:"var(--text-2)"}}>Created</div>
                </div>
              )}
              {importResult.updated > 0 && (
                <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(107,156,255,0.06)",border:"1px solid rgba(107,156,255,0.2)"}}>
                  <div style={{fontSize:24,fontWeight:700,color:"#2563eb"}} className="mono">{importResult.updated}</div>
                  <div style={{fontSize:12,color:"var(--text-2)"}}>Updated</div>
                </div>
              )}
              {importResult.skipped > 0 && (
                <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid var(--border)"}}>
                  <div style={{fontSize:24,fontWeight:700,color:"var(--text-3)"}} className="mono">{importResult.skipped}</div>
                  <div style={{fontSize:12,color:"var(--text-2)"}}>Skipped</div>
                </div>
              )}
              {importResult.errors.length > 0 && (
                <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)"}}>
                  <div style={{fontSize:24,fontWeight:700,color:"#dc2626"}} className="mono">{importResult.errors.length}</div>
                  <div style={{fontSize:12,color:"var(--text-2)"}}>Errors</div>
                </div>
              )}
            </div>

            {importResult.errors.length > 0 && (
              <div style={{textAlign:"left",marginTop:16}}>
                <div style={{fontSize:12,fontWeight:600,marginBottom:6}}>Errors:</div>
                {importResult.errors.map((e, i) => (
                  <div key={i} style={{fontSize:11,color:"#dc2626",padding:"4px 0",borderBottom:"1px solid var(--border)"}}>
                    Row {e.row} (Aadhaar: {e.aadhaar || "—"}): {e.error}
                  </div>
                ))}
              </div>
            )}

            <div style={{marginTop:20,display:"flex",gap:8,justifyContent:"center"}}>
              <button className="btn" onClick={() => { setStep("upload"); setCsvHeaders([]); setCsvRows([]); setPreviewData([]); setMapping({}); setImportResult(null); }} data-testid="bulk-new-import">New Import</button>
              <button className="btn primary" onClick={() => window.location.href = "/"} data-testid="bulk-to-directory">Go to Directory</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EXPERIENCE IMPORT ===== */}
      {mode === "experience" && expStep === "upload" && (
        <div style={{maxWidth:560,margin:"40px auto",textAlign:"center"}}>
          <div style={{marginBottom:18}}>
            <button className="btn primary" onClick={downloadExpTemplate} data-testid="exp-download-template" style={{fontSize:13,padding:"10px 24px"}}>
              Download Experience CSV Template
            </button>
            <div style={{fontSize:11,color:"var(--text-3)",marginTop:6}}>Career journey format: Name, Designation, From, To, Institution</div>
          </div>
          <div
            style={{border:"2px dashed var(--border)",borderRadius:14,padding:"48px 24px",cursor:"pointer",marginBottom:16}}
            onClick={() => expFileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleExpFile(e.dataTransfer.files[0]); }}
            data-testid="exp-dropzone"
          >
            <div style={{fontSize:32,marginBottom:8,color:"var(--text-3)"}}>CSV</div>
            <div style={{fontSize:14,fontWeight:500}}>Drop Experience CSV here or click to browse</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginTop:6}}>Multiple rows per faculty — each row is one career position</div>
          </div>
          <input ref={expFileRef} type="file" accept=".csv" style={{display:"none"}} onChange={e => handleExpFile(e.target.files[0])} data-testid="exp-file-input"/>
          <div style={{background:"rgba(107,156,255,0.06)",border:"1px solid rgba(107,156,255,0.2)",borderRadius:8,padding:"12px 16px",fontSize:12,color:"var(--text-2)",textAlign:"left"}}>
            <strong>How it works:</strong>
            <ol style={{margin:"6px 0 0 16px",padding:0,lineHeight:1.8}}>
              <li>Upload a CSV where each row is a <strong>position held</strong> (Tutor, SR, Asst Prof, etc.)</li>
              <li>Multiple rows per faculty name — the system groups them automatically</li>
              <li>Faculty matched by <strong>name</strong> (or Aadhaar if column present) to existing records</li>
              <li>Career positions are appended to each faculty's Past Experience</li>
            </ol>
            <div style={{marginTop:8,color:"var(--text-3)",fontSize:11}}>Rows marked "Till Date" or "Present" in the To column are treated as current positions and excluded from past experience.</div>
          </div>
          {/* Experience Batch History */}
          {batches.filter(b => b.batch_type === "experience").length > 0 && (
            <div style={{marginTop:30}}>
              <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:1,color:"var(--text-3)",fontWeight:600,marginBottom:10}}>Experience Import History</div>
              {batches.filter(b => b.batch_type === "experience").map(b => (
                <div key={b.batch_id} style={{border:"1px solid var(--border)",borderRadius:10,padding:"12px 16px",marginBottom:8,opacity:b.deleted?0.4:1}} data-testid={`exp-batch-${b.batch_id}`}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontWeight:500,fontSize:13}}>{b.file_name} {b.deleted && <span style={{color:"#dc2626",fontSize:11}}>(reverted)</span>}</div>
                      <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>
                        {new Date(b.created_at).toLocaleString()} · {b.created_by}
                      </div>
                      <div style={{fontSize:11,color:"var(--text-2)",marginTop:2}}>
                        <span style={{color:"#2563eb"}}>{b.updated} faculty updated</span>
                        {b.skipped > 0 && <span> · {b.skipped} skipped</span>}
                        {b.deleted && <span> · {b.deleted_count} reverted to original</span>}
                      </div>
                    </div>
                    {!b.deleted && b.updated > 0 && (
                      <button className="btn" style={{fontSize:11,padding:"4px 12px",color:"#dc2626",flexShrink:0}}
                        onClick={() => { setUndoTarget(b); setUndoPin(""); setUndoErr(""); }}
                        data-testid={`undo-exp-${b.batch_id}`}
                      >Undo Batch</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "experience" && expStep === "preview" && (
        <div style={{margin:"20px auto"}}>
          <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
            <div style={{padding:"8px 16px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)",fontSize:13}}>
              <strong style={{color:"#059669"}}>{expGroups.filter(g => g.match && !g.skip).length}</strong> faculty matched
            </div>
            <div style={{padding:"8px 16px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)",fontSize:13}}>
              <strong style={{color:"#dc2626"}}>{expGroups.filter(g => !g.match).length}</strong> unmatched
            </div>
            <div style={{padding:"8px 16px",borderRadius:8,background:"rgba(107,156,255,0.06)",border:"1px solid rgba(107,156,255,0.2)",fontSize:13}}>
              <strong style={{color:"#2563eb"}}>{expGroups.reduce((a,g) => a + (g.skip ? 0 : g.pastPositions.length), 0)}</strong> positions to import
            </div>
          </div>

          {expGroups.map((g, gi) => (
            <div key={gi} style={{border:"1px solid var(--border)",borderRadius:10,padding:"14px 16px",marginBottom:10,opacity:g.skip?0.4:1}} data-testid={`exp-group-${gi}`}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div>
                  <div style={{fontWeight:600,fontSize:14}}>{g.name} {g.aadhaar && <span className="mono" style={{fontSize:11,color:"var(--text-3)",fontWeight:400}}>· Aadhaar: …{g.aadhaar.slice(-4)}</span>}</div>
                  <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>
                    {g.match
                      ? <span style={{color:"#059669"}}>Matched → {g.match.ssmch_id} · {g.match.department} · {g.match.designation}{g.existingExpCount > 0 ? ` · ${g.existingExpCount} existing positions` : ""}</span>
                      : <span style={{color:"#dc2626"}}>No match found in directory — will skip</span>
                    }
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  {g.match && (
                    <button className="btn" style={{fontSize:10,padding:"2px 8px",color:g.skip?"#059669":"var(--text-3)"}}
                      onClick={() => { const u = [...expGroups]; u[gi] = {...u[gi], skip:!u[gi].skip}; setExpGroups(u); }}
                    >{g.skip ? "Include" : "Skip"}</button>
                  )}
                </div>
              </div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:"1px solid var(--border)",background:"var(--bg-alt)"}}>
                    <th style={{padding:"4px 8px",textAlign:"left"}}>Designation</th>
                    <th style={{padding:"4px 8px",textAlign:"left"}}>From</th>
                    <th style={{padding:"4px 8px",textAlign:"left"}}>To</th>
                    <th style={{padding:"4px 8px",textAlign:"left"}}>Institution</th>
                    <th style={{padding:"4px 8px",textAlign:"center"}}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {g.positions.map((p, pi) => (
                    <tr key={pi} style={{borderBottom:"1px solid var(--border)",opacity:p.is_current?0.5:1}}>
                      <td style={{padding:"4px 8px",fontWeight:500}}>{p.designation}</td>
                      <td style={{padding:"4px 8px"}} className="mono">{p.from_date}</td>
                      <td style={{padding:"4px 8px"}} className="mono">{p.to_date || "Till Date"}</td>
                      <td style={{padding:"4px 8px"}}>
                        {p.institution}
                        {!p.is_current && <span style={{display:'inline-flex',alignItems:'center',gap:3,marginLeft:6,fontSize:10,color:'#991b1b'}}><span style={{width:6,height:6,borderRadius:'50%',background:'var(--bad)',display:'inline-block',flexShrink:0}}/> NMC Unverified</span>}
                      </td>
                      <td style={{padding:"4px 8px",textAlign:"center",fontSize:10}}>
                        {p.is_current
                          ? <span style={{color:"var(--text-3)",fontStyle:"italic"}}>Current (skip)</span>
                          : <span style={{color:"#059669"}}>Past</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <div style={{display:"flex",justifyContent:"space-between",marginTop:16}}>
            <button className="btn" onClick={() => setExpStep("upload")}>Back</button>
            <button className="btn primary" disabled={expImporting || expGroups.filter(g => g.match && !g.skip).length === 0}
              onClick={executeExpImport} data-testid="exp-execute-btn">
              {expImporting ? "Importing…" : `Import ${expGroups.filter(g => g.match && !g.skip).reduce((a,g)=>a+g.pastPositions.length,0)} positions for ${expGroups.filter(g => g.match && !g.skip).length} faculty`}
            </button>
          </div>
        </div>
      )}

      {mode === "experience" && expStep === "result" && expResult && (
        <div style={{maxWidth:560,margin:"40px auto"}}>
          <div className="form-section" style={{padding:24,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>Experience Import Complete</div>
            <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:16,flexWrap:"wrap"}}>
              <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)"}}>
                <div style={{fontSize:24,fontWeight:700,color:"#059669"}} className="mono">{expResult.updated}</div>
                <div style={{fontSize:12,color:"var(--text-2)"}}>Faculty Updated</div>
              </div>
              {expResult.skipped > 0 && (
                <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid var(--border)"}}>
                  <div style={{fontSize:24,fontWeight:700,color:"var(--text-3)"}} className="mono">{expResult.skipped}</div>
                  <div style={{fontSize:12,color:"var(--text-2)"}}>Skipped</div>
                </div>
              )}
              {expResult.errors.length > 0 && (
                <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)"}}>
                  <div style={{fontSize:24,fontWeight:700,color:"#dc2626"}} className="mono">{expResult.errors.length}</div>
                  <div style={{fontSize:12,color:"var(--text-2)"}}>Errors</div>
                </div>
              )}
            </div>
            {expResult.errors.length > 0 && (
              <div style={{textAlign:"left",marginTop:16}}>
                {expResult.errors.map((e, i) => (
                  <div key={i} style={{fontSize:11,color:"#dc2626",padding:"4px 0"}}>{e.name}: {e.error}</div>
                ))}
              </div>
            )}
            <div style={{marginTop:20}}>
              <button className="btn" onClick={() => { setExpStep("upload"); setExpGroups([]); setExpResult(null); }}>New Import</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ AEBAS ATTENDANCE TAB ═══════ */}

      {mode === "aebas" && aebasStep === "upload" && !aebasMappingStep && (
        <div style={{maxWidth:620,margin:"40px auto",textAlign:"center"}}>
          {/* AEBAS ID Mapping section */}
          <div style={{marginBottom:28,padding:20,background:"var(--surface)",border:"1px solid var(--line)",borderRadius:10}}>
            <div style={{fontSize:15,fontWeight:600,marginBottom:8}}>Step 1: Map AEBAS IDs to Faculty</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginBottom:14}}>Upload a CSV with <strong>Aadhaar Number</strong> and <strong>AEBAS ID</strong> columns. The system matches directly against the database.</div>
            <div
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) parseAebasMappingCsv(f); }}
              onDragOver={e => e.preventDefault()}
              onClick={() => aebasMappingRef.current?.click()}
              style={{border:"2px dashed var(--border)",borderRadius:8,padding:"28px 16px",cursor:"pointer",background:"var(--bg-alt)"}}
              data-testid="aebas-mapping-drop"
            >
              <input ref={aebasMappingRef} type="file" accept=".csv" hidden onChange={e => { const f = e.target.files[0]; if (f) parseAebasMappingCsv(f); }}/>
              <div style={{fontSize:13,fontWeight:500}}>Drop Aadhaar ↔ AEBAS ID Mapping CSV</div>
            </div>
          </div>

          {/* AEBAS Attendance Upload section */}
          <div style={{padding:20,background:"var(--surface)",border:"1px solid var(--line)",borderRadius:10}}>
            <div style={{fontSize:15,fontWeight:600,marginBottom:8}}>Step 2: Upload AEBAS Attendance</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginBottom:14}}>Upload the NMC Portal attendance export CSV. Matches by AEBAS ID only.</div>
            <div
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) parseAebasCsv(f); }}
              onDragOver={e => e.preventDefault()}
              onClick={() => aebasFileRef.current?.click()}
              style={{border:"2px dashed var(--border)",borderRadius:8,padding:"28px 16px",cursor:"pointer",background:"var(--bg-alt)"}}
              data-testid="aebas-drop-zone"
            >
              <input ref={aebasFileRef} type="file" accept=".csv" hidden onChange={e => { const f = e.target.files[0]; if (f) parseAebasCsv(f); }} data-testid="aebas-file-input"/>
              <div style={{fontSize:13,fontWeight:500}}>Drop NMC Portal Attendance CSV</div>
            </div>
          </div>
        </div>
      )}

      {/* AEBAS Mapping Preview/Result */}
      {mode === "aebas" && aebasMappingStep === "preview" && (
        <div style={{maxWidth:620,margin:"20px auto"}}>
          <div style={{marginBottom:14,fontSize:13,color:"var(--text-2)"}}>
            Parsed <strong className="mono">{aebasMappingRecords.length}</strong> Aadhaar ↔ AEBAS ID pairs. This will match directly against the database.
          </div>
          <div style={{maxHeight:300,overflowY:"auto",border:"1px solid var(--border)",borderRadius:8,marginBottom:16}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead style={{position:"sticky",top:0}}><tr style={{background:"var(--bg-alt)",borderBottom:"1px solid var(--border)"}}>
                <th style={{padding:"6px 10px",textAlign:"left"}}>#</th>
                <th style={{padding:"6px 10px",textAlign:"left"}}>Aadhaar</th>
                <th style={{padding:"6px 10px",textAlign:"left"}}>AEBAS ID</th>
              </tr></thead>
              <tbody>
                {aebasMappingRecords.slice(0,50).map((r,i) => (
                  <tr key={i} style={{borderBottom:"1px solid var(--line-soft)"}}>
                    <td style={{padding:"5px 10px"}} className="mono">{i+1}</td>
                    <td style={{padding:"5px 10px"}} className="mono">{r.aadhaar}</td>
                    <td style={{padding:"5px 10px"}} className="mono">{r.aebas_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <button className="btn" onClick={() => { setAebasMappingStep(null); setAebasMappingRecords([]); }}>Back</button>
            <button className="btn primary" onClick={executeAebasMapping} data-testid="aebas-mapping-confirm">
              Map {aebasMappingRecords.length} AEBAS IDs
            </button>
          </div>
        </div>
      )}

      {mode === "aebas" && aebasMappingStep === "importing" && (
        <div style={{padding:60,textAlign:"center"}}><div className="spinner"/><div style={{marginTop:12,fontSize:13,color:"var(--text-3)"}}>Mapping AEBAS IDs...</div></div>
      )}

      {mode === "aebas" && aebasMappingStep === "result" && aebasMappingResult && (
        <div style={{maxWidth:600,margin:"40px auto"}}>
          <div className="form-section" style={{padding:24}}>
            {aebasMappingResult.error ? (
              <div style={{color:"var(--bad)",fontSize:14,textAlign:"center"}}>{aebasMappingResult.error}</div>
            ) : (
              <>
                <div style={{fontSize:24,textAlign:"center",marginBottom:16}}>AEBAS ID Mapping Complete</div>
                <div style={{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap"}}>
                  <div style={{padding:"10px 18px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)",textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:700,color:"#059669"}} className="mono">{aebasMappingResult.updated}</div>
                    <div style={{fontSize:11,color:"var(--text-2)"}}>Updated</div>
                  </div>
                  <div style={{padding:"10px 18px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid var(--border)",textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:700,color:"var(--text-3)"}} className="mono">{aebasMappingResult.already_set}</div>
                    <div style={{fontSize:11,color:"var(--text-2)"}}>Already Set</div>
                  </div>
                  <div style={{padding:"10px 18px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)",textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:700,color:"#dc2626"}} className="mono">{aebasMappingResult.skipped_no_match}</div>
                    <div style={{fontSize:11,color:"var(--text-2)"}}>Aadhaar Not Found</div>
                  </div>
                </div>
                {(aebasMappingResult.unmatched||[]).length > 0 && (
                  <div style={{marginTop:16,maxHeight:200,overflowY:"auto",border:"1px solid var(--line-soft)",borderRadius:6,padding:8}}>
                    <div style={{fontSize:12,fontWeight:600,marginBottom:4}}>Unmatched Aadhaar numbers:</div>
                    {aebasMappingResult.unmatched.map((u,i) => (
                      <div key={i} style={{fontSize:11,padding:"2px 0",color:"var(--text-3)",fontFamily:"var(--mono)"}}>{u.aadhaar} → {u.aebas_id}</div>
                    ))}
                  </div>
                )}
              </>
            )}
            <div style={{marginTop:20,textAlign:"center"}}>
              <button className="btn" onClick={() => { setAebasMappingStep(null); setAebasMappingRecords([]); setAebasMappingResult(null); }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {mode === "aebas" && aebasStep === "preview" && (
        <div>
          <div style={{marginBottom:14,fontSize:13,color:"var(--text-2)"}}>
            Parsed <strong className="mono">{aebasRecords.length}</strong> attendance records from <strong>{aebasFile}</strong>.
          </div>
          <div style={{overflowX:"auto",border:"1px solid var(--border)",borderRadius:8,maxHeight:440,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}} data-testid="aebas-preview-table">
              <thead style={{position:"sticky",top:0,zIndex:1}}>
                <tr style={{background:"var(--bg-alt)",borderBottom:"1px solid var(--border)"}}>
                  <th style={{padding:"6px 8px",textAlign:"left"}}>#</th>
                  <th style={{padding:"6px 8px",textAlign:"left"}}>AEBAS ID</th>
                  <th style={{padding:"6px 8px",textAlign:"left"}}>Name</th>
                  <th style={{padding:"6px 8px",textAlign:"left"}}>Designation</th>
                  <th style={{padding:"6px 8px",textAlign:"left"}}>Department</th>
                  <th style={{padding:"6px 8px",textAlign:"left"}}>Status</th>
                  <th style={{padding:"6px 8px",textAlign:"left"}}>In Time</th>
                </tr>
              </thead>
              <tbody>
                {aebasRecords.slice(0, 100).map((r, i) => (
                  <tr key={i} style={{borderBottom:"1px solid var(--line-soft)"}}>
                    <td style={{padding:"5px 8px"}} className="mono">{r.sno || i+1}</td>
                    <td style={{padding:"5px 8px"}} className="mono">{r.attendance_id}</td>
                    <td style={{padding:"5px 8px",fontWeight:500}}>{r.user_name}</td>
                    <td style={{padding:"5px 8px"}}>{r.designation}</td>
                    <td style={{padding:"5px 8px"}}>{r.department}</td>
                    <td style={{padding:"5px 8px"}}><span className={`badge ${r.status==='P'?'ok':r.status==='A'?'bad':'warn'}`}>{r.status}</span></td>
                    <td style={{padding:"5px 8px",fontSize:11}} className="mono">{r.in_time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {aebasRecords.length > 100 && <div style={{fontSize:12,color:"var(--text-3)",marginTop:8}}>Showing first 100 of {aebasRecords.length} records</div>}
          <div style={{display:"flex",justifyContent:"space-between",marginTop:16}}>
            <button className="btn" onClick={() => { setAebasStep("upload"); setAebasRecords([]); }}>Back</button>
            <button className="btn primary" disabled={aebasImporting} onClick={executeAebasImport} data-testid="aebas-confirm-import">
              {aebasImporting ? "Processing..." : `Process & Match · ${aebasRecords.length} records`}
            </button>
          </div>
        </div>
      )}

      {mode === "aebas" && aebasStep === "result" && aebasResult && (
        <div style={{maxWidth:700,margin:"40px auto"}}>
          <div className="form-section" style={{padding:24}}>
            {aebasResult.error ? (
              <div style={{color:"var(--bad)",fontSize:14,textAlign:"center"}}>{aebasResult.error}</div>
            ) : (
              <>
                <div style={{fontSize:28,textAlign:"center",marginBottom:12}}>AEBAS Data Processed</div>
                <div style={{display:"flex",gap:16,justifyContent:"center",flexWrap:"wrap",marginBottom:20}}>
                  <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)",textAlign:"center"}}>
                    <div style={{fontSize:24,fontWeight:700,color:"#059669"}} className="mono">{aebasResult.matched}</div>
                    <div style={{fontSize:12,color:"var(--text-2)"}}>Matched by AEBAS ID</div>
                  </div>
                  <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid var(--border)",textAlign:"center"}}>
                    <div style={{fontSize:24,fontWeight:700,color:"var(--text-3)"}} className="mono">{aebasResult.skipped}</div>
                    <div style={{fontSize:12,color:"var(--text-2)"}}>Skipped (non-faculty)</div>
                  </div>
                  <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)",textAlign:"center"}}>
                    <div style={{fontSize:24,fontWeight:700,color:"#dc2626"}} className="mono">{(aebasResult.not_found||[]).length}</div>
                    <div style={{fontSize:12,color:"var(--text-2)"}}>Not Found in FIMS</div>
                  </div>
                </div>
                {(aebasResult.faculty_without_aebas_id||[]).length > 0 && (
                  <div style={{marginTop:12,padding:12,background:"rgba(234,179,8,0.06)",border:"1px solid rgba(234,179,8,0.2)",borderRadius:8}}>
                    <div style={{fontSize:13,fontWeight:600,marginBottom:6,color:"#ca8a04"}}>Faculty Missing AEBAS ID ({(aebasResult.faculty_without_aebas_id||[]).length})</div>
                    <div style={{fontSize:12,color:"var(--text-2)",marginBottom:6}}>These faculty don't have an AEBAS ID in FIMS. Upload their AEBAS IDs via Faculty Data bulk import (with Aadhaar + AEBAS ID columns).</div>
                    <div style={{maxHeight:160,overflowY:"auto"}}>
                      {(aebasResult.faculty_without_aebas_id||[]).map((f, i) => (
                        <div key={i} style={{fontSize:12,padding:"2px 0",color:"var(--text-2)"}}>{f.full_name} <span className="mono" style={{fontSize:10,color:"var(--text-3)"}}>{f.ssmch_id}</span></div>
                      ))}
                    </div>
                  </div>
                )}
                {(aebasResult.not_found||[]).length > 0 && (
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Unmatched Names</div>
                    <div style={{maxHeight:200,overflowY:"auto",border:"1px solid var(--line-soft)",borderRadius:6,padding:8}}>
                      {aebasResult.not_found.map((nf, i) => (
                        <div key={i} style={{fontSize:12,padding:"3px 0",color:"var(--text-2)"}}>{nf.name} — {nf.designation} · {nf.department}</div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            <div style={{marginTop:20,textAlign:"center"}}>
              <button className="btn" onClick={() => { setAebasStep("upload"); setAebasRecords([]); setAebasResult(null); }}>New Upload</button>
              <a href="/aebas-discrepancies" className="btn primary" style={{marginLeft:12,textDecoration:"none",display:"inline-block"}}>View Discrepancies</a>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ NMC COLLEGES TAB ═══════ */}

      {mode === "nmc" && nmcStep === "upload" && (
        <div style={{maxWidth:620,margin:"40px auto",textAlign:"center"}}>
          <div style={{marginBottom:18}}>
            <button className="btn primary" onClick={downloadNmcTemplate} data-testid="nmc-download-template" style={{fontSize:13,padding:"10px 24px"}}>
              Download NMC CSV Template
            </button>
          </div>
          <div
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) parseNmcCsv(f); }}
            onDragOver={e => e.preventDefault()}
            onClick={() => nmcFileRef.current?.click()}
            style={{border:"2px dashed var(--border)",borderRadius:12,padding:"48px 24px",cursor:"pointer",background:"var(--bg-alt)",transition:"border-color 0.2s"}}
            data-testid="nmc-drop-zone"
          >
            <input ref={nmcFileRef} type="file" accept=".csv" hidden onChange={e => { const f = e.target.files[0]; if (f) parseNmcCsv(f); }} data-testid="nmc-file-input"/>
            <div style={{fontSize:14,fontWeight:500}}>Drop NMC Colleges CSV here or click to browse</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginTop:8}}>CSV with columns: S No., Course Name, State, College Code, College Name, University, Management, Year, Intake, NMC Status, Date of LOP</div>
          </div>
          <div style={{fontSize:12,color:"var(--text-3)",marginTop:14,textAlign:"left",lineHeight:1.7}}>
            <strong>Important:</strong> Uploading replaces the entire NMC college database and auto-verifies all faculty past experience records.
          </div>
          {nmcStats && (
            <div style={{marginTop:18,padding:14,background:"var(--bg-alt)",borderRadius:8,border:"1px solid var(--border)",fontSize:13,color:"var(--text-2)"}}>
              Current database: <strong className="mono">{nmcStats.total}</strong> courses · <strong className="mono">{nmcStats.unique_colleges}</strong> unique colleges · <strong className="mono">{nmcStats.states}</strong> states
            </div>
          )}
        </div>
      )}

      {mode === "nmc" && nmcStep === "preview" && (
        <div>
          <div style={{marginBottom:14,fontSize:13,color:"var(--text-2)"}}>
            Parsed <strong className="mono">{nmcRecords.length}</strong> course records from <strong>{nmcFile}</strong>. This will <span style={{color:"var(--bad)",fontWeight:600}}>replace</span> the entire existing NMC database.
          </div>
          <div style={{overflowX:"auto",border:"1px solid var(--border)",borderRadius:8,maxHeight:440,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}} data-testid="nmc-preview-table">
              <thead style={{position:"sticky",top:0,zIndex:1}}>
                <tr style={{background:"var(--bg-alt)",borderBottom:"1px solid var(--border)"}}>
                  <th style={{padding:"6px 8px",textAlign:"left",whiteSpace:"nowrap"}}>#</th>
                  <th style={{padding:"6px 8px",textAlign:"left",whiteSpace:"nowrap"}}>Course</th>
                  <th style={{padding:"6px 8px",textAlign:"left",whiteSpace:"nowrap"}}>State</th>
                  <th style={{padding:"6px 8px",textAlign:"left",whiteSpace:"nowrap"}}>Code</th>
                  <th style={{padding:"6px 8px",textAlign:"left",minWidth:200}}>College / Institution</th>
                  <th style={{padding:"6px 8px",textAlign:"left"}}>University</th>
                  <th style={{padding:"6px 8px",textAlign:"left",whiteSpace:"nowrap"}}>Mgmt</th>
                  <th style={{padding:"6px 8px",textAlign:"center",whiteSpace:"nowrap"}}>Year</th>
                  <th style={{padding:"6px 8px",textAlign:"center",whiteSpace:"nowrap"}}>Intake</th>
                  <th style={{padding:"6px 8px",textAlign:"left",whiteSpace:"nowrap"}}>NMC Status</th>
                  <th style={{padding:"6px 8px",textAlign:"left",whiteSpace:"nowrap"}}>LOP Date</th>
                </tr>
              </thead>
              <tbody>
                {nmcRecords.slice(0, 100).map((r, i) => (
                  <tr key={i} style={{borderBottom:"1px solid var(--line-soft)"}}>
                    <td style={{padding:"5px 8px"}} className="mono">{r.sno || i + 1}</td>
                    <td style={{padding:"5px 8px"}}>{r.course_name}</td>
                    <td style={{padding:"5px 8px"}}>{r.state}</td>
                    <td style={{padding:"5px 8px"}} className="mono">{r.college_code}</td>
                    <td style={{padding:"5px 8px",fontWeight:500}}>{r.college}</td>
                    <td style={{padding:"5px 8px",fontSize:11}}>{r.university}</td>
                    <td style={{padding:"5px 8px",fontSize:11}}>{r.management}</td>
                    <td style={{padding:"5px 8px",textAlign:"center"}} className="mono">{r.year_of_inception}</td>
                    <td style={{padding:"5px 8px",textAlign:"center"}} className="mono">{r.annual_intake}</td>
                    <td style={{padding:"5px 8px",fontSize:11}}>{r.nmc_recognition_status}</td>
                    <td style={{padding:"5px 8px",fontSize:11}} className="mono">{r.date_of_lop}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {nmcRecords.length > 100 && <div style={{fontSize:12,color:"var(--text-3)",marginTop:8}}>Showing first 100 of {nmcRecords.length} records</div>}
          <div style={{display:"flex",justifyContent:"space-between",marginTop:16}}>
            <button className="btn" onClick={() => { setNmcStep("upload"); setNmcRecords([]); }}>Back</button>
            <button className="btn primary" disabled={nmcImporting} onClick={executeNmcImport} data-testid="nmc-confirm-import">
              {nmcImporting ? "Importing..." : `Replace Database · ${nmcRecords.length} course records`}
            </button>
          </div>
        </div>
      )}

      {mode === "nmc" && nmcStep === "result" && nmcResult && (
        <div style={{maxWidth:560,margin:"40px auto"}}>
          <div className="form-section" style={{padding:24,textAlign:"center"}}>
            {nmcResult.error ? (
              <div style={{color:"var(--bad)",fontSize:14}}>{nmcResult.error}</div>
            ) : (
              <>
                <div style={{fontSize:28,marginBottom:8}}>NMC Database Replaced</div>
                <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:16,flexWrap:"wrap"}}>
                  <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)"}}>
                    <div style={{fontSize:24,fontWeight:700,color:"#059669"}} className="mono">{nmcResult.courses_imported}</div>
                    <div style={{fontSize:12,color:"var(--text-2)"}}>Courses Imported</div>
                  </div>
                  <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)"}}>
                    <div style={{fontSize:24,fontWeight:700,color:"#059669"}} className="mono">{nmcResult.unique_colleges}</div>
                    <div style={{fontSize:12,color:"var(--text-2)"}}>Unique Colleges</div>
                  </div>
                  <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)"}}>
                    <div style={{fontSize:24,fontWeight:700,color:"#059669"}} className="mono">{nmcResult.institutions_verified_yes}</div>
                    <div style={{fontSize:12,color:"var(--text-2)"}}>Faculty Verified NMC</div>
                  </div>
                  <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)"}}>
                    <div style={{fontSize:24,fontWeight:700,color:"#dc2626"}} className="mono">{nmcResult.institutions_verified_no}</div>
                    <div style={{fontSize:12,color:"var(--text-2)"}}>Not NMC Recognised</div>
                  </div>
                  <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid var(--border)"}}>
                    <div style={{fontSize:24,fontWeight:700,color:"var(--text-2)"}} className="mono">{nmcResult.faculty_reverified}</div>
                    <div style={{fontSize:12,color:"var(--text-2)"}}>Faculty Records Updated</div>
                  </div>
                </div>
              </>
            )}
            <div style={{marginTop:20}}>
              <button className="btn" onClick={() => { setNmcStep("upload"); setNmcRecords([]); setNmcResult(null); loadNmcStats(); }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ QUALIFICATIONS TAB ═══════ */}

      {mode === "qualifications" && qualStep === "upload" && (
        <div style={{maxWidth:620,margin:"40px auto",textAlign:"center"}}>
          <div style={{marginBottom:18}}>
            <button className="btn primary" onClick={downloadQualTemplate} data-testid="qual-download-template" style={{fontSize:13,padding:"10px 24px"}}>
              Download Qualifications CSV Template
            </button>
          </div>
          <div
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) parseQualCsv(f); }}
            onDragOver={e => e.preventDefault()}
            onClick={() => qualFileRef.current?.click()}
            style={{border:"2px dashed var(--border)",borderRadius:12,padding:"48px 24px",cursor:"pointer",background:"var(--bg-alt)"}}
            data-testid="qual-drop-zone"
          >
            <input ref={qualFileRef} type="file" accept=".csv" hidden onChange={e => { const f = e.target.files[0]; if (f) parseQualCsv(f); }} data-testid="qual-file-input"/>
            <div style={{fontSize:14,fontWeight:500}}>Drop Qualifications CSV here or click to browse</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginTop:8}}>Columns: Aadhaar #, Faculty Name, Degree Level (UG/PG/Diploma/DNB/PhD), Degree Type, Degree, College, Year, Council, Reg Number, Reg Date</div>
          </div>
          <div style={{fontSize:12,color:"var(--text-3)",marginTop:14,textAlign:"left",lineHeight:1.7}}>
            <ul style={{paddingLeft:16,margin:0}}>
              <li>Faculty matched by <strong>Aadhaar #</strong> (primary) or Name (fallback)</li>
              <li><strong>Degree Level</strong> determines target field: UG, PG, Diploma, DNB, or PhD</li>
              <li>One faculty can have multiple rows (one per qualification)</li>
              <li>Conflicts shown in preview — choose to keep existing or use CSV value</li>
            </ul>
          </div>
        </div>
      )}

      {mode === "qualifications" && qualStep === "preview" && (
        <div>
          <div style={{marginBottom:14,fontSize:13,color:"var(--text-2)"}}>
            Parsed <strong className="mono">{qualGroups.reduce((a,g)=>a+g.quals.length,0)}</strong> qualification records for <strong className="mono">{qualGroups.length}</strong> faculty from <strong>{qualFile}</strong>.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}} data-testid="qual-preview-list">
            {qualGroups.map((g, gi) => (
              <div key={gi} style={{border:"1px solid var(--border)",borderRadius:8,padding:14,background: g.match ? "var(--surface)" : "rgba(248,113,113,0.05)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div>
                    <strong>{g.name}</strong>
                    {g.aadhaar && <span className="mono" style={{fontSize:11,color:"var(--text-3)",marginLeft:8}}>Aadhaar: {g.aadhaar}</span>}
                  </div>
                  {g.match
                    ? <span className="badge ok">Matched · {g.match.ssmch_id}</span>
                    : <span className="badge bad">No match found</span>
                  }
                </div>
                {g.quals.map((q, qi) => {
                  const hasConflicts = Object.keys(q.conflicts).length > 0;
                  return (
                    <div key={qi} style={{padding:"8px 0",borderTop: qi > 0 ? "1px solid var(--line-soft)" : "none"}}>
                      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                        <span className="badge teal" style={{fontSize:10}}>{q.level}</span>
                        <span style={{fontSize:13,fontWeight:500}}>{q.degree}</span>
                        {hasConflicts && <span className="badge warn" style={{fontSize:9.5}}>Conflicts</span>}
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))",gap:6,fontSize:12}}>
                        {Object.entries(q.fields).filter(([k]) => k !== q.fields.flag && !k.startsWith("has_")).map(([field, val]) => {
                          const conflict = q.conflicts[field];
                          const decKey = `${gi}-${field}`;
                          const decision = qualDecisions[decKey] || (conflict ? "csv" : "csv");
                          const fieldLabel = field.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                          return (
                            <div key={field} style={{padding:"4px 8px",borderRadius:4,background: conflict ? "rgba(234,179,8,0.08)" : "var(--bg-elev)",border: conflict ? "1px solid rgba(234,179,8,0.3)" : "1px solid var(--line-soft)"}}>
                              <div style={{fontSize:10,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:"0.04em"}}>{fieldLabel}</div>
                              {conflict ? (
                                <div style={{marginTop:2}}>
                                  <div style={{display:"flex",gap:4,marginBottom:2}}>
                                    <button onClick={() => setQualDecisions(d => ({...d, [decKey]: "csv"}))}
                                      style={{fontSize:10,padding:"2px 6px",border:"1px solid",borderRadius:4,cursor:"pointer",
                                        background: decision === "csv" ? "rgba(52,211,153,0.15)" : "transparent",
                                        borderColor: decision === "csv" ? "#059669" : "var(--border)",
                                        color: decision === "csv" ? "#059669" : "var(--text-3)",fontWeight: decision === "csv" ? 600 : 400}}>
                                      Use CSV
                                    </button>
                                    <button onClick={() => setQualDecisions(d => ({...d, [decKey]: "keep"}))}
                                      style={{fontSize:10,padding:"2px 6px",border:"1px solid",borderRadius:4,cursor:"pointer",
                                        background: decision === "keep" ? "rgba(59,130,246,0.15)" : "transparent",
                                        borderColor: decision === "keep" ? "#3b82f6" : "var(--border)",
                                        color: decision === "keep" ? "#3b82f6" : "var(--text-3)",fontWeight: decision === "keep" ? 600 : 400}}>
                                      Keep Existing
                                    </button>
                                  </div>
                                  <div style={{fontSize:11}}>
                                    <span style={{color: decision === "csv" ? "#059669" : "var(--text-3)", textDecoration: decision !== "csv" ? "line-through" : "none"}}>CSV: {val}</span>
                                    <span style={{margin:"0 4px",color:"var(--text-3)"}}>vs</span>
                                    <span style={{color: decision === "keep" ? "#3b82f6" : "var(--text-3)", textDecoration: decision !== "keep" ? "line-through" : "none"}}>Existing: {conflict}</span>
                                  </div>
                                </div>
                              ) : (
                                <div style={{fontSize:12,marginTop:1,color:"var(--text-1)"}}>{val}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:16}}>
            <button className="btn" onClick={() => { setQualStep("upload"); setQualGroups([]); }}>Back</button>
            <button className="btn primary" disabled={qualImporting || qualGroups.filter(g=>g.match).length===0} onClick={executeQualImport} data-testid="qual-confirm-import">
              {qualImporting ? "Importing..." : `Import Qualifications · ${qualGroups.filter(g=>g.match).length} faculty`}
            </button>
          </div>
        </div>
      )}

      {mode === "qualifications" && qualStep === "result" && qualResult && (
        <div style={{maxWidth:560,margin:"40px auto"}}>
          <div className="form-section" style={{padding:24,textAlign:"center"}}>
            <div style={{fontSize:28,marginBottom:8}}>Qualifications Import Complete</div>
            <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:16,flexWrap:"wrap"}}>
              <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)"}}>
                <div style={{fontSize:24,fontWeight:700,color:"#059669"}} className="mono">{qualResult.updated}</div>
                <div style={{fontSize:12,color:"var(--text-2)"}}>Faculty Updated</div>
              </div>
              {qualResult.skipped > 0 && (
                <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid var(--border)"}}>
                  <div style={{fontSize:24,fontWeight:700,color:"var(--text-3)"}} className="mono">{qualResult.skipped}</div>
                  <div style={{fontSize:12,color:"var(--text-2)"}}>Skipped</div>
                </div>
              )}
              {qualResult.errors.length > 0 && (
                <div style={{padding:"12px 20px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)"}}>
                  <div style={{fontSize:24,fontWeight:700,color:"#dc2626"}} className="mono">{qualResult.errors.length}</div>
                  <div style={{fontSize:12,color:"var(--text-2)"}}>Errors</div>
                </div>
              )}
            </div>
            {qualResult.errors.length > 0 && (
              <div style={{textAlign:"left",marginTop:16}}>
                {qualResult.errors.map((e, i) => (
                  <div key={i} style={{fontSize:11,color:"#dc2626",padding:"4px 0"}}>{e.name}: {e.error}</div>
                ))}
              </div>
            )}
            <div style={{marginTop:20}}>
              <button className="btn" onClick={() => { setQualStep("upload"); setQualGroups([]); setQualResult(null); }}>New Import</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ AADHAAR RESTORE TAB ═══════ */}

      {mode === "restore" && restoreStep === "upload" && (
        <div style={{maxWidth:580,margin:"40px auto",textAlign:"center"}}>
          <div style={{marginBottom:20,padding:16,background:"rgba(248,113,113,0.06)",border:"1px solid rgba(248,113,113,0.2)",borderRadius:10,textAlign:"left"}}>
            <div style={{fontSize:14,fontWeight:600,color:"#dc2626",marginBottom:6}}>⚠ Aadhaar Data Recovery</div>
            <div style={{fontSize:12,color:"var(--text-2)",lineHeight:1.6}}>
              Some Aadhaar numbers in the database may have been overwritten with masked values (e.g. <code style={{background:"var(--bg-alt)",padding:"1px 4px",borderRadius:3}}>••••••••5107</code>). 
              Upload a CSV containing the correct <strong>Full Name</strong> and <strong>Aadhaar Number</strong> to restore them. The system matches by faculty name.
            </div>
          </div>
          <div style={{marginBottom:18}}>
            <button className="btn" onClick={() => {
              const csv = "Full Name,Aadhaar Number\nExample Faculty,123456789012\n";
              const blob = new Blob([csv], { type: "text/csv" });
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
              a.download = "Aadhaar_Restore_Template.csv"; a.click(); URL.revokeObjectURL(a.href);
            }} data-testid="restore-download-template" style={{fontSize:13,padding:"10px 24px"}}>
              Download Template
            </button>
          </div>
          <div
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) parseRestoreCsv(f); }}
            onDragOver={e => e.preventDefault()}
            onClick={() => restoreFileRef.current?.click()}
            style={{border:"2px dashed var(--border)",borderRadius:14,padding:"48px 24px",cursor:"pointer",marginBottom:16}}
            data-testid="restore-drop-zone"
          >
            <input ref={restoreFileRef} type="file" accept=".csv" hidden onChange={e => { const f = e.target.files[0]; if (f) parseRestoreCsv(f); }} data-testid="restore-file-input"/>
            <div style={{fontSize:14,fontWeight:500,marginBottom:6}}>Drop CSV with Name & Aadhaar</div>
            <div style={{fontSize:11,color:"var(--text-3)"}}>Columns: Full Name (or Name), Aadhaar Number (12-digit)</div>
          </div>
        </div>
      )}

      {mode === "restore" && restoreStep === "preview" && (
        <div style={{maxWidth:620,margin:"20px auto"}}>
          <div style={{marginBottom:14,fontSize:13,color:"var(--text-2)"}}>
            Parsed <strong className="mono">{restoreRecords.length}</strong> name ↔ Aadhaar pairs. The system will match by faculty name and restore clean Aadhaar values.
          </div>
          <div style={{maxHeight:360,overflowY:"auto",border:"1px solid var(--border)",borderRadius:8,marginBottom:16}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}} data-testid="restore-preview-table">
              <thead style={{position:"sticky",top:0}}><tr style={{background:"var(--bg-alt)",borderBottom:"1px solid var(--border)"}}>
                <th style={{padding:"6px 10px",textAlign:"left"}}>#</th>
                <th style={{padding:"6px 10px",textAlign:"left"}}>Name</th>
                <th style={{padding:"6px 10px",textAlign:"left"}}>Aadhaar</th>
              </tr></thead>
              <tbody>
                {restoreRecords.slice(0, 100).map((r, i) => (
                  <tr key={i} style={{borderBottom:"1px solid var(--line-soft)"}}>
                    <td style={{padding:"5px 10px"}} className="mono">{i + 1}</td>
                    <td style={{padding:"5px 10px"}}>{r.name}</td>
                    <td style={{padding:"5px 10px"}} className="mono">{r.aadhaar}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <button className="btn" onClick={() => { setRestoreStep("upload"); setRestoreRecords([]); }} data-testid="restore-back-btn">Back</button>
            <button className="btn primary" onClick={executeRestore} data-testid="restore-confirm-btn">
              Restore {restoreRecords.length} Aadhaar Numbers
            </button>
          </div>
        </div>
      )}

      {mode === "restore" && restoreStep === "importing" && (
        <div style={{padding:60,textAlign:"center"}}><div className="spinner"/><div style={{marginTop:12,fontSize:13,color:"var(--text-3)"}}>Restoring Aadhaar data...</div></div>
      )}

      {mode === "restore" && restoreStep === "result" && restoreResult && (
        <div style={{maxWidth:600,margin:"40px auto"}}>
          <div className="form-section" style={{padding:24}}>
            {restoreResult.error ? (
              <div style={{color:"var(--bad)",fontSize:14,textAlign:"center"}} data-testid="restore-error">{restoreResult.error}</div>
            ) : (
              <>
                <div style={{fontSize:24,textAlign:"center",marginBottom:16}} data-testid="restore-result-title">Aadhaar Restore Complete</div>
                <div style={{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap"}}>
                  <div style={{padding:"10px 18px",borderRadius:8,background:"rgba(52,211,153,0.08)",border:"1px solid rgba(52,211,153,0.2)",textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:700,color:"#059669"}} className="mono" data-testid="restore-count">{restoreResult.restored}</div>
                    <div style={{fontSize:11,color:"var(--text-2)"}}>Restored</div>
                  </div>
                  <div style={{padding:"10px 18px",borderRadius:8,background:"rgba(0,0,0,0.03)",border:"1px solid var(--border)",textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:700,color:"var(--text-3)"}} className="mono">{restoreResult.already_clean}</div>
                    <div style={{fontSize:11,color:"var(--text-2)"}}>Already Clean</div>
                  </div>
                  <div style={{padding:"10px 18px",borderRadius:8,background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.2)",textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:700,color:"#dc2626"}} className="mono" data-testid="restore-not-found">{restoreResult.not_found}</div>
                    <div style={{fontSize:11,color:"var(--text-2)"}}>Not Found</div>
                  </div>
                </div>
                {(restoreResult.unmatched || []).length > 0 && (
                  <div style={{marginTop:16}}>
                    <div style={{fontSize:12,fontWeight:600,color:"#dc2626",marginBottom:6}}>Unmatched Names:</div>
                    <div style={{maxHeight:200,overflowY:"auto",fontSize:11,color:"var(--text-2)"}}>
                      {restoreResult.unmatched.map((u, i) => (
                        <div key={i} style={{padding:"3px 0"}}>{u.name} — ••••••••{u.aadhaar?.slice(-4)}</div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            <div style={{marginTop:20,textAlign:"center"}}>
              <button className="btn" onClick={() => { setRestoreStep("upload"); setRestoreRecords([]); setRestoreResult(null); }} data-testid="restore-new-btn">New Restore</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
